import { z } from "zod";
import { Locale, SupportedMarketplaceCurrency } from "@repo/i18n";

//#region src/account.ts
/**
* Seller account status values.
* - active: fully operational (default when field is absent)
* - paused: seller self-paused, listings hidden, can unpause
* - suspended: admin action, listings hidden, fund hold activated
* - banned: permanent admin action, listings cancelled, fund hold activated
*/
const SELLER_ACCOUNT_STATUSES = [
	"active",
	"paused",
	"suspended",
	"banned"
];
const SellerAccountStatusValueSchema = z.enum(SELLER_ACCOUNT_STATUSES);
/**
* Buyer account status values.
* - active: can purchase (default when field is absent)
* - suspended: admin action, cannot purchase
* - banned: permanent admin action, cannot purchase
*/
const BUYER_ACCOUNT_STATUSES = [
	"active",
	"suspended",
	"banned"
];
const BuyerAccountStatusValueSchema = z.enum(BUYER_ACCOUNT_STATUSES);
const AccountStatusSchema = (statusValues) => z.object({
	status: z.enum(statusValues),
	reason: z.string().optional(),
	changedBy: z.string().optional(),
	changedAt: z.union([z.date(), z.string()]).optional()
});

//#endregion
//#region src/address-match.ts
const COMBINING_MARKS = /[̀-ͯ]/g;
const NON_ALPHANUMERIC = /[^\p{L}\p{N}]+/gu;
function normaliseText(value) {
	if (!value) return "";
	return value.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase().replace(NON_ALPHANUMERIC, " ").trim().replace(/\s+/g, " ");
}
function normaliseCountry(value) {
	return (value ?? "").trim().toUpperCase();
}
/**
* US ZIP+4 codes ("94107-1234") are compared on their five-digit base, since
* a document and a delivery address often carry only one of the two forms.
*/
function normalisePostalCode(value, country) {
	const compact = normaliseText(value).replace(/\s/g, "");
	if (country === "US") return compact.slice(0, 5);
	return compact;
}
/**
* Compare the address read from an identity document with a delivery address.
*
* Text is lowercased, stripped of accents and punctuation, and whitespace is
* collapsed; postal codes are compared without spaces.
*
* @param idAddress - address on the identity document; `null` when the document has none
* @param deliveryAddress - delivery address of the order
* @returns `match` when country, postal code and line 1 are equal; `partial`
*   when only country and postal code are; `no_match` otherwise; `unavailable`
*   when the document carries no address
*/
function compareAddresses(idAddress, deliveryAddress) {
	const idCountry = normaliseCountry(idAddress?.countryCode);
	const idPostalCode = normalisePostalCode(idAddress?.postalCode, idCountry);
	const idLine1 = normaliseText(idAddress?.line1);
	if (!idCountry && !idPostalCode && !idLine1) return "unavailable";
	const deliveryCountry = normaliseCountry(deliveryAddress.countryCode);
	const deliveryPostalCode = normalisePostalCode(deliveryAddress.postalCode, deliveryCountry);
	const deliveryLine1 = normaliseText(deliveryAddress.line1);
	if (!(idCountry !== "" && idCountry === deliveryCountry) || !(idPostalCode !== "" && idPostalCode === deliveryPostalCode)) return "no_match";
	return idLine1 !== "" && idLine1 === deliveryLine1 ? "match" : "partial";
}

//#endregion
//#region src/admin-identifier.ts
const HEX_ID_RE = /^[0-9a-f]{24}$/i;
const ORDER_NUMBER_RE = /^OR-[0-9A-Z]{8}-\d+$/i;
const TRANSACTION_NUMBER_RE = /^TX-[0-9A-HJKMNP-TV-Z]{8}$/i;
const CLERK_USER_ID_RE = /^user_[A-Za-z0-9]+$/;
const STRIPE_BODY_RE = /^[A-Za-z0-9_]+$/;
const NUMERIC_RE = /^\d{1,10}$/;
const DIGITS_ONLY_RE = /^\d+$/;
const CODE_RE = /^[A-Z0-9][A-Z0-9-]{1,11}$/;
const SLUG_RE = /^[a-z0-9][a-z0-9._-]{1,63}$/;
const SLUG_ANY_CASE_RE = /^[a-z0-9][a-z0-9._-]{1,63}$/i;
const MAX_NUMERIC = 2147483647;
const STRIPE_PREFIXES = [
	["acct_", "stripeAccount"],
	["cus_", "stripeCustomer"],
	["cs_", "stripeCheckoutSession"],
	["pi_", "stripePaymentIntent"],
	["ch_", "stripeCharge"],
	["dp_", "stripeOther"],
	["tr_", "stripeOther"],
	["re_", "stripeOther"],
	["po_", "stripeOther"],
	["txn_", "stripeOther"],
	["py_", "stripeOther"],
	["ba_", "stripeOther"],
	["card_", "stripeOther"]
];
const PATH_ENTITY_SEGMENTS = new Set([
	"users",
	"orders",
	"transactions",
	"offers",
	"moderation",
	"coupons",
	"integration-apps"
]);
function isEmail(token) {
	const at = token.indexOf("@");
	return at !== -1 && token.indexOf(".", at + 1) !== -1;
}
function classifyStripe(token) {
	for (const [prefix, kind] of STRIPE_PREFIXES) if (token.startsWith(prefix) && STRIPE_BODY_RE.test(token.slice(prefix.length))) return kind;
}
function isIpv4(token) {
	const parts = token.split(".");
	if (parts.length !== 4) return false;
	return parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
}
function isIpv6(token) {
	return token.split(":").length >= 3 && /^[0-9a-fA-F:]+$/.test(token);
}
function isCidr(token) {
	const slash = token.lastIndexOf("/");
	if (slash === -1) return false;
	const prefix = token.slice(slash + 1);
	if (!/^\d{1,3}$/.test(prefix)) return false;
	const address = token.slice(0, slash);
	return isIpv4(address) || isIpv6(address);
}
function pathnameFromUrl(url) {
	try {
		return new URL(url).pathname;
	} catch {
		return url;
	}
}
function tokenFromPath(path) {
	const segments = (path.split(/[?#]/)[0] ?? "").split("/").filter((segment) => segment.length > 0);
	if (segments.length === 0) return "";
	const first = segments[0];
	const second = segments[1];
	if (first !== void 0 && second !== void 0 && PATH_ENTITY_SEGMENTS.has(first)) return second;
	const last = segments[segments.length - 1] ?? "";
	if (first === "explore") {
		const publicId = last.match(/-(\d+)$/)?.[1];
		if (publicId !== void 0 && Number(publicId) >= 1e4) return publicId;
	}
	return last;
}
function stripChatChannelForms(token) {
	let result = token;
	if (result.endsWith("-support-buyer")) result = result.slice(0, -14);
	else if (result.endsWith("-support-seller")) result = result.slice(0, -15);
	if (result.startsWith("negotiation-") && HEX_ID_RE.test(result.slice(12))) return result.slice(12);
	if (result.startsWith("order-") && ORDER_NUMBER_RE.test(result.slice(6))) return result.slice(6);
	return result;
}
function classifyToken(token, original) {
	const candidate = stripChatChannelForms(token.startsWith("@") ? token.slice(1) : token);
	if (isEmail(candidate)) return {
		kind: "email",
		value: candidate.toLowerCase(),
		original
	};
	if (HEX_ID_RE.test(candidate)) return {
		kind: "hexId",
		value: candidate.toLowerCase(),
		original
	};
	if (ORDER_NUMBER_RE.test(candidate)) return {
		kind: "orderNumber",
		value: candidate.toUpperCase(),
		original
	};
	if (TRANSACTION_NUMBER_RE.test(candidate)) return {
		kind: "transactionNumber",
		value: candidate.toUpperCase(),
		original
	};
	if (CLERK_USER_ID_RE.test(candidate)) return {
		kind: "clerkUserId",
		value: candidate,
		original
	};
	const stripeKind = classifyStripe(candidate);
	if (stripeKind !== void 0) return {
		kind: stripeKind,
		value: candidate,
		original
	};
	if (isCidr(candidate)) return {
		kind: "cidr",
		value: candidate,
		original
	};
	if (isIpv4(candidate) || isIpv6(candidate)) return {
		kind: "ip",
		value: candidate,
		original
	};
	if (NUMERIC_RE.test(candidate) && Number(candidate) <= MAX_NUMERIC) return {
		kind: "numeric",
		value: candidate,
		original
	};
	if (CODE_RE.test(candidate) && !DIGITS_ONLY_RE.test(candidate)) return {
		kind: "code",
		value: candidate,
		original
	};
	if (SLUG_RE.test(candidate)) return {
		kind: "slug",
		value: candidate,
		original
	};
	if (SLUG_ANY_CASE_RE.test(candidate)) return {
		kind: "slug",
		value: candidate.toLowerCase(),
		original
	};
	return {
		kind: "text",
		value: candidate,
		original
	};
}
function classifyAdminIdentifier(raw) {
	const trimmed = raw.trim();
	if (trimmed === "") return {
		kind: "text",
		value: "",
		original: raw
	};
	let token = trimmed;
	if (token.startsWith("http://") || token.startsWith("https://")) token = pathnameFromUrl(token);
	if (token.startsWith("/")) token = tokenFromPath(token);
	return classifyToken(token, raw);
}

//#endregion
//#region src/affiliate-link.ts
/** Host of the clean affiliate links creators share. */
const AFFILIATE_LINK_HOST = "af.cardnexus.link";
/** Query params Impact appends to the landing URL: its click id and the Worker's correlation token. */
const AFFILIATE_CLICK_ID_PARAM = "im_ref";
const AFFILIATE_AF_ID_PARAM = "af_id";
/** Product id sources an affiliate link can name. */
const AFFILIATE_ID_SOURCES = [
	"cn",
	"tcg",
	"cm"
];
/** Segment prefixing an optional game scope: `/{mpId}/g/{game}/...`. */
const GAME_SCOPE_SEGMENT = "g";
/** Segment introducing a products-list link: `/{mpId}/products/{source}/{lines}`. */
const PRODUCTS_SEGMENT = "products";
/** Segment introducing a profile link: `/{mpId}/u/{username}`. */
const USER_SEGMENT = "u";
/** Query params of the `/go` landing route, as the Worker builds them. */
const AFFILIATE_LANDING_PARAMS = {
	cn: "cn",
	tcg: "tcg",
	cm: "cm",
	query: "q",
	game: "game",
	ids: "ids",
	products: "products",
	user: "u",
	listing: "listing",
	locale: "locale",
	country: "country"
};
/** Whether a path segment names one of the product id sources. */
function isAffiliateIdSource(value) {
	return AFFILIATE_ID_SOURCES.some((source) => source === value);
}
function withGame(destination, game) {
	return game ? {
		...destination,
		game
	} : destination;
}
function decodeSegment(segment) {
	try {
		return decodeURIComponent(segment);
	} catch {
		return segment;
	}
}
/**
* Parses the path of a clean affiliate link with the same grammar as the
* `af.cardnexus.link` Worker: `/{mpId}[/g/{game}]` followed by
* `{cn|tcg|cm}/{id}[/{name}]`, `products/{cn|tcg|cm}/{lines}`, `u/{username}`,
* free-text search segments, or nothing.
*
* @returns null when the path carries no media partner id
*/
function parseAffiliateLinkPath(pathname) {
	const segments = pathname.split("/").filter(Boolean);
	const mpId = segments[0];
	if (!mpId) return null;
	let cursor = 1;
	let game;
	if (segments[cursor] === GAME_SCOPE_SEGMENT && segments[cursor + 1]) {
		game = segments[cursor + 1];
		cursor += 2;
	}
	const rest = segments.slice(cursor);
	const [head, second, third] = rest;
	if (head === PRODUCTS_SEGMENT && isAffiliateIdSource(second) && third !== void 0) return {
		mpId,
		destination: withGame({
			shape: "products",
			source: second,
			lines: decodeSegment(third)
		}, game)
	};
	if (head === USER_SEGMENT && second !== void 0) return {
		mpId,
		destination: withGame({
			shape: "user",
			username: decodeSegment(second).toLowerCase()
		}, game)
	};
	if (isAffiliateIdSource(head) && second !== void 0) {
		const name = rest.slice(2).map(decodeSegment).join(" ");
		return {
			mpId,
			destination: withGame({
				shape: "product",
				source: head,
				id: second,
				...name ? { name } : {}
			}, game)
		};
	}
	if (rest.length > 0) return {
		mpId,
		destination: withGame({
			shape: "search",
			query: decodeSegment(rest.join("-"))
		}, game)
	};
	return {
		mpId,
		destination: withGame({ shape: "general" }, game)
	};
}
/**
* Parses the query of the `/go` landing route into the same destination the
* clean link expressed, with the precedence the route applies: a products
* list, then a profile, then a CardNexus id, then a TCGplayer or Cardmarket
* id, then the search query.
*
* @returns null when the query names no destination
*/
function parseAffiliateLandingParams(searchParams) {
	const game = searchParams.get(AFFILIATE_LANDING_PARAMS.game)?.trim() || void 0;
	const lines = searchParams.get(AFFILIATE_LANDING_PARAMS.products);
	if (lines) {
		const ids = searchParams.get(AFFILIATE_LANDING_PARAMS.ids);
		return withGame({
			shape: "products",
			source: isAffiliateIdSource(ids) ? ids : "cn",
			lines
		}, game);
	}
	const username = searchParams.get(AFFILIATE_LANDING_PARAMS.user)?.trim();
	if (username) return withGame({
		shape: "user",
		username: username.toLowerCase()
	}, game);
	for (const source of AFFILIATE_ID_SOURCES) {
		const id = searchParams.get(AFFILIATE_LANDING_PARAMS[source])?.trim();
		if (!id) continue;
		const name = searchParams.get(AFFILIATE_LANDING_PARAMS.query)?.trim();
		return withGame({
			shape: "product",
			source,
			id,
			...name ? { name } : {}
		}, game);
	}
	const query = searchParams.get(AFFILIATE_LANDING_PARAMS.query)?.trim();
	if (query) return withGame({
		shape: "search",
		query
	}, game);
	return null;
}
/** The Impact click id and Worker token carried by a landing or hand-off URL, when both are present. */
function readAffiliateAttribution(searchParams) {
	const clickId = searchParams.get(AFFILIATE_CLICK_ID_PARAM)?.trim();
	if (!clickId) return null;
	const afId = searchParams.get(AFFILIATE_AF_ID_PARAM)?.trim();
	return afId ? {
		clickId,
		afId
	} : { clickId };
}

//#endregion
//#region src/api-access-request.ts
/**
* Lifecycle of a request for API access. `needs-info` waits on the applicant
* answering a staff question and returns to `pending` on their reply.
* `approved` and `rejected` are terminal; a user may file a new request once
* their previous one is decided.
*/
const API_ACCESS_REQUEST_STATUSES = [
	"pending",
	"approved",
	"rejected",
	"needs-info"
];
/**
* Statuses of a request still awaiting a decision. A user has at most one
* request in any of them, and staff can decide or question only these.
*/
const API_ACCESS_REQUEST_OPEN_STATUSES = ["pending", "needs-info"];
/** Whether a request in `status` still awaits a decision. */
function isOpenApiAccessRequestStatus(status) {
	return API_ACCESS_REQUEST_OPEN_STATUSES.includes(status);
}
/** Who wrote a message on a request: staff asking, or the applicant answering. */
const API_ACCESS_REQUEST_MESSAGE_AUTHOR_TYPES = ["staff", "applicant"];
/**
* Where a request row comes from: the API access form, or a historical row
* the cutover backfill wrote for an account that got access from the private
* preview sheet (`preview-sheet`) or by its flag alone (`flag-only`). Only
* `form` rows carry the form answers.
*/
const API_ACCESS_REQUEST_SOURCES = [
	"form",
	"preview-sheet",
	"flag-only"
];
/** The outcomes staff can record: the lifecycle minus `pending`. */
const API_ACCESS_REQUEST_DECISIONS = ["approved", "rejected"];
/**
* Who the applicant builds for: their own CardNexus account, or a tool or
* platform other people use.
*/
const API_ACCESS_REQUEST_AUDIENCES = ["own-account", "third-party-tool"];
/** Kind of tool a `third-party-tool` applicant builds. */
const API_ACCESS_REQUEST_TOOL_CATEGORIES = [
	"deck-builder",
	"inventory-management",
	"collection-tracker",
	"price-tracking",
	"other"
];
/** Parts of the public API the applicant expects to use. */
const API_ACCESS_REQUEST_API_AREAS = [
	"catalogues-and-prices",
	"inventory-sync",
	"listings",
	"sales-and-purchases"
];
/**
* Access a request asks for or is granted: `personal` is the `public-api` flag
* on the applicant's own account, `app` is an integration app.
*/
const API_ACCESS_KINDS = ["personal", "app"];
/**
* Outcome of giving an approved applicant the API role in the CardNexus
* Discord server. `member-not-found` and `ambiguous` come from matching the
* Discord username they typed; `not-configured` means the backend lacks the
* Discord settings.
*/
const API_ACCESS_REQUEST_DISCORD_GRANT_STATUSES = [
	"granted",
	"member-not-found",
	"ambiguous",
	"not-configured",
	"failed"
];
const API_ACCESS_REQUEST_PROJECT_NAME_MAX_LENGTH = 100;
const API_ACCESS_REQUEST_WEBSITE_URL_MAX_LENGTH = 500;
const API_ACCESS_REQUEST_DESCRIPTION_MAX_LENGTH = 2e3;
const API_ACCESS_REQUEST_DISCORD_USERNAME_MAX_LENGTH = 40;
const API_ACCESS_REQUEST_DECISION_NOTES_MAX_LENGTH = 2e3;
const API_ACCESS_REQUEST_REJECTION_REASON_MAX_LENGTH = 2e3;
const API_ACCESS_REQUEST_MESSAGE_MAX_LENGTH = 2e3;
/** The access an audience asks for: tools need an app, everyone else personal access. */
function requestedAccessForAudience(audience) {
	return audience === "third-party-tool" ? "app" : "personal";
}

//#endregion
//#region src/api-data-permission.ts
/**
* Data permissions staff grant to an integration app or to a personal account.
* They sit on top of scopes: a key or app keeps its scopes, and these are
* checked in addition.
*
* - `market-values`: the market value blocks of other marketplaces on
*   products, search and the price snapshot.
* - `price-history`: the daily price history endpoint.
* - `bulk-market-values`: the full daily price file; without it the price
*   file link points to the CardNexus-only file.
*/
const API_DATA_PERMISSIONS = [
	"market-values",
	"price-history",
	"bulk-market-values"
];
const ApiDataPermissionSchema = z.enum(API_DATA_PERMISSIONS);
/**
* Why a grant exists: an account request, an app scope request, an API
* access request approval, a staff switch, the first-party seed of migration
* 0152, or the cutover backfill.
*/
const API_DATA_PERMISSION_GRANT_SOURCES = [
	"permission-request",
	"scope-request",
	"access-request",
	"admin",
	"first-party",
	"cutover"
];
const ApiDataPermissionGrantSourceSchema = z.enum(API_DATA_PERMISSION_GRANT_SOURCES);
/**
* Lifecycle of an account's request for data permissions. Only `pending`
* awaits staff; `withdrawn` covers a request the requester withdrew or staff
* closed with the account's access.
*/
const API_DATA_PERMISSION_REQUEST_STATUSES = [
	"pending",
	"accepted",
	"rejected",
	"withdrawn"
];
const ApiDataPermissionRequestStatusSchema = z.enum(API_DATA_PERMISSION_REQUEST_STATUSES);
/** Longest staff note on a grant switch or a request decision. */
const API_DATA_PERMISSION_NOTE_MAX_LENGTH = 2e3;
/**
* Who holds a data permission: a personal account (its personal keys) or an
* integration app (its credentials, on-behalf calls and grant tokens).
*/
const ApiDataPermissionHolderSchema = z.discriminatedUnion("kind", [z.object({
	kind: z.literal("account"),
	userId: z.string()
}), z.object({
	kind: z.literal("app"),
	appId: z.string()
})]);
/**
* How staff changed a holder's data access: a switch removed price data, or
* "Close access" ended the account's API access altogether.
*/
const API_DATA_ACCESS_CHANGES = ["price-data-removed", "access-closed"];
const ApiDataAccessChangeSchema = z.enum(API_DATA_ACCESS_CHANGES);
/** Where a permission stands for its holder, as the requester sees it. */
const API_DATA_PERMISSION_STATES = [
	"granted",
	"requested",
	"not-granted",
	"declined"
];
/** Who sees what the project builds with the data. */
const API_DATA_APPLICATION_AUDIENCES = [
	"only-me",
	"own-users",
	"public"
];
const ApiDataApplicationAudienceSchema = z.enum(API_DATA_APPLICATION_AUDIENCES);
/** How many people use the project each month. */
const API_DATA_APPLICATION_MONTHLY_USERS = [
	"just-me",
	"under-1k",
	"1k-to-10k",
	"10k-to-100k",
	"over-100k"
];
const ApiDataApplicationMonthlyUsersSchema = z.enum(API_DATA_APPLICATION_MONTHLY_USERS);
/** How many calls a day the requester expects for one permission. */
const API_DATA_APPLICATION_DAILY_CALLS = [
	"under-1k",
	"1k-to-10k",
	"over-10k"
];
const ApiDataApplicationDailyCallsSchema = z.enum(API_DATA_APPLICATION_DAILY_CALLS);
/** What CardNexus gets from the project, as the requester claims it. */
const API_DATA_APPLICATION_BENEFITS = [
	"links-to-cardnexus",
	"prices-by-cardnexus",
	"manages-seller-stock",
	"sells-on-cardnexus",
	"buys-on-cardnexus",
	"brings-new-members",
	"nothing-yet"
];
const ApiDataApplicationBenefitSchema = z.enum(API_DATA_APPLICATION_BENEFITS);
/** Length and count limits of the application answers. */
const API_DATA_APPLICATION_LIMITS = {
	projectDescriptionMin: 200,
	usageDescriptionMin: 100,
	benefitsExplanationMin: 100,
	textMax: 4e3,
	otherMarketplacesMax: 500,
	pageUrlsMax: 10,
	urlMax: 2048
};
/** An `http` or `https` URL the requester gives. */
const ApplicationUrlSchema = z.url({ protocol: /^https?$/ }).max(API_DATA_APPLICATION_LIMITS.urlMax);
/** A free-text answer of at least `min` characters once trimmed. */
function applicationText(min) {
	return z.string().trim().min(min).max(API_DATA_APPLICATION_LIMITS.textMax);
}
/**
* Section 1, the project. Unless it is for the requester alone (`only-me`),
* it needs a live `url`, or `notLiveYet` with an `expectedLaunch` month
* (`YYYY-MM`).
*/
const ApiDataApplicationProjectSchema = z.object({
	description: applicationText(API_DATA_APPLICATION_LIMITS.projectDescriptionMin),
	audience: ApiDataApplicationAudienceSchema,
	url: ApplicationUrlSchema.nullable(),
	notLiveYet: z.boolean(),
	expectedLaunch: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).nullable(),
	monthlyUsers: ApiDataApplicationMonthlyUsersSchema
}).superRefine((project, ctx) => {
	if (project.audience === "only-me") return;
	if (!project.notLiveYet && project.url === null) ctx.addIssue({
		code: "custom",
		path: ["url"],
		message: "A link to the project is required"
	});
	if (project.notLiveYet && project.expectedLaunch === null) ctx.addIssue({
		code: "custom",
		path: ["expectedLaunch"],
		message: "An expected launch month is required"
	});
});
/**
* Section 2, asked once per permission: what the data is used for, the pages
* that show it, the expected daily calls, and whether other marketplaces'
* prices or links appear next to it.
*/
const ApiDataApplicationUsageSchema = z.object({
	description: applicationText(API_DATA_APPLICATION_LIMITS.usageDescriptionMin),
	pageUrls: z.array(ApplicationUrlSchema).max(API_DATA_APPLICATION_LIMITS.pageUrlsMax),
	dailyCalls: ApiDataApplicationDailyCallsSchema,
	otherMarketplaces: z.discriminatedUnion("shown", [z.object({ shown: z.literal(false) }), z.object({
		shown: z.literal(true),
		which: z.string().trim().min(1).max(API_DATA_APPLICATION_LIMITS.otherMarketplacesMax)
	})])
});
/** Section 3, what CardNexus gets: at least one claim, explained. */
const ApiDataApplicationBenefitsSchema = z.object({
	benefits: z.array(ApiDataApplicationBenefitSchema).min(1).refine((benefits) => new Set(benefits).size === benefits.length, { message: "Benefits must be unique" }),
	explanation: applicationText(API_DATA_APPLICATION_LIMITS.benefitsExplanationMin)
});
/** Section 4, the four commitments; each must be accepted. */
const ApiDataApplicationCommitmentsSchema = z.object({
	showsAttribution: z.literal(true),
	noRedistribution: z.literal(true),
	reappliesForNewUse: z.literal(true),
	allowsPageChecks: z.literal(true)
});
/**
* The application sent with an account request or an app scope request that
* adds a data permission. `usage` has one entry per permission asked for;
* contracts check that with `apiDataApplicationIssues`. Stored as is, with no
* dates inside.
*/
const ApiDataApplicationSchema = z.object({
	project: ApiDataApplicationProjectSchema,
	usage: z.partialRecord(ApiDataPermissionSchema, ApiDataApplicationUsageSchema),
	benefits: ApiDataApplicationBenefitsSchema,
	commitments: ApiDataApplicationCommitmentsSchema
});
/**
* Sections 2 to 4 of the application, asked on the API access form, whose
* own questions already describe the project.
*/
const ApiDataAccessRequestApplicationSchema = ApiDataApplicationSchema.omit({ project: true });
/**
* Checks that an application covers exactly the permissions it is sent
* with: one usage block per permission and none other, each listing at least
* one page unless `pageUrlsOptional`. Returns no issue when it does.
* Contracts report the issues from `.superRefine`, prefixing each path with
* the application's field.
*/
function apiDataApplicationIssues({ permissions, usage, pageUrlsOptional }) {
	const issues = [];
	const asked = new Set(permissions);
	for (const permission of API_DATA_PERMISSIONS) {
		const block = usage[permission];
		if (!asked.has(permission)) {
			if (block) issues.push({
				path: ["usage", permission],
				code: "usage-unexpected"
			});
			continue;
		}
		if (!block) {
			issues.push({
				path: ["usage", permission],
				code: "usage-missing"
			});
			continue;
		}
		if (!pageUrlsOptional && block.pageUrls.length === 0) issues.push({
			path: [
				"usage",
				permission,
				"pageUrls"
			],
			code: "page-urls-required"
		});
	}
	return issues;
}
/**
* The state of each permission, in `API_DATA_PERMISSIONS` order. A held
* permission is `granted`; else one in the pending request is `requested`;
* else the latest decision that asked for it decides: `declined` when it was
* rejected or accepted without granting it, `not-granted` when it was granted
* and later removed. A permission never asked for is `not-granted`.
*/
function resolveApiDataPermissionStates({ held, pending, decisions }) {
	return API_DATA_PERMISSIONS.map((permission) => {
		const view = {
			permission,
			state: "not-granted",
			grantedAt: null,
			requestedAt: null,
			declinedAt: null,
			declineNote: null
		};
		const heldEntry = held.find((entry) => entry.permission === permission);
		if (heldEntry) return {
			...view,
			state: "granted",
			grantedAt: heldEntry.grantedAt
		};
		if (pending?.permissions.includes(permission)) return {
			...view,
			state: "requested",
			requestedAt: pending.submittedAt
		};
		const latest = latestDecisionFor(decisions, permission);
		if (!latest) return view;
		if (latest.status === "accepted" && (latest.granted ?? []).includes(permission)) return view;
		return {
			...view,
			state: "declined",
			declinedAt: latest.decidedAt,
			declineNote: latest.note
		};
	});
}
/** The most recent decision on a request that asked for `permission`. */
function latestDecisionFor(decisions, permission) {
	let latest = null;
	for (const decision of decisions) {
		if (!decision.requested.includes(permission)) continue;
		if (!latest || decision.decidedAt.getTime() > latest.decidedAt.getTime()) latest = decision;
	}
	return latest;
}

//#endregion
//#region src/api-key-scope.ts
const PUBLIC_API_SCOPES = [
	"*",
	"inventory:read",
	"inventory:write",
	"listings:read",
	"listings:write",
	"lists:read",
	"lists:write",
	"cart:read",
	"cart:write",
	"offers:read",
	"offers:write",
	"sales:read",
	"sales:write",
	"purchases:read",
	"purchases:write",
	"disputes:read",
	"disputes:write",
	"messaging:read",
	"messaging:write",
	"account:read",
	"account:write",
	"financial:read"
];
const PublicApiScopeSchema = z.enum(PUBLIC_API_SCOPES);
/**
* Scopes that must never be granted to a third-party integration on a user's
* behalf: `*` is full access, first-party keys only. Catalogue, pricing, and
* marketplace data need no scope at all — those endpoints declare
* `scopes: []` and require only a valid credential.
*/
const NON_GRANTABLE_SCOPES = ["*"];
/** The scopes an integration application may actually be granted. */
const INTEGRATION_GRANTABLE_SCOPES = PUBLIC_API_SCOPES.filter((scope) => !NON_GRANTABLE_SCOPES.includes(scope));
/**
* Svix feature-flag name for a scope, e.g. `inventory:read` →
* `scope-inventory-read`. Event types in the Svix catalogue are tagged with
* the flags of the scopes that can receive them, and an App Portal session is
* minted carrying only the flags its holder actually has — so the portal's
* Event Catalog offers exactly the events that endpoint is allowed to get.
*/
function scopeFeatureFlag(scope) {
	return `scope-${scope.replace(":", "-")}`;
}
/** Every scope flag — what the account owner's own portal is minted with. */
const ALL_SCOPE_FEATURE_FLAGS = INTEGRATION_GRANTABLE_SCOPES.map(scopeFeatureFlag);
function scopesToFeatureFlags(scopes) {
	if (scopes.includes("*")) return [...ALL_SCOPE_FEATURE_FLAGS];
	return scopes.map(scopeFeatureFlag);
}
/**
* Grantable scopes grouped by resource, each with a read and (where it exists)
* a write level. One source of truth for the consent screen and the admin
* scope picker, so a user sees the same grouping we mint.
*/
const PUBLIC_API_SCOPE_GROUPS = [
	{
		key: "offers",
		label: "Offers",
		read: "offers:read",
		write: "offers:write",
		readSummary: "View your sent and received offers.",
		writeSummary: "View, send, and respond to your offers."
	},
	{
		key: "account",
		label: "Account",
		read: "account:read",
		write: "account:write",
		readSummary: "View your account profile.",
		writeSummary: "View and update your account profile and vacation mode."
	},
	{
		key: "inventory",
		label: "Inventory",
		read: "inventory:read",
		write: "inventory:write",
		readSummary: "View your inventory.",
		writeSummary: "View, add, edit, and remove your inventory."
	},
	{
		key: "listings",
		label: "Listings",
		read: "listings:read",
		write: "listings:write",
		readSummary: "View your listings.",
		writeSummary: "View, publish, and update your listings."
	},
	{
		key: "lists",
		label: "Lists",
		read: "lists:read",
		write: "lists:write",
		readSummary: "View your lists.",
		writeSummary: "View, create, and edit your lists."
	},
	{
		key: "cart",
		label: "Cart",
		read: "cart:read",
		write: "cart:write",
		readSummary: "View your cart.",
		writeSummary: "View and modify your cart."
	},
	{
		key: "sales",
		label: "Sales",
		read: "sales:read",
		write: "sales:write",
		readSummary: "View your sales.",
		writeSummary: "View and manage your sales."
	},
	{
		key: "purchases",
		label: "Purchases",
		read: "purchases:read",
		write: "purchases:write",
		readSummary: "View your purchases.",
		writeSummary: "View and manage your purchases."
	},
	{
		key: "disputes",
		label: "Disputes",
		read: "disputes:read",
		write: "disputes:write",
		readSummary: "View your disputes.",
		writeSummary: "View and manage your disputes."
	},
	{
		key: "messaging",
		label: "Messaging",
		read: "messaging:read",
		write: "messaging:write",
		readSummary: "Read your messages.",
		writeSummary: "Read and send messages on your behalf."
	},
	{
		key: "financial",
		label: "Financial",
		read: "financial:read",
		write: null,
		readSummary: "View your wallet balance and payouts.",
		writeSummary: ""
	}
];
/**
* Collapse a flat scope list into one entry per resource group at its highest
* granted level (write implies read). Groups with no granted scope are omitted.
* Used by the consent screen so a user sees "Inventory — Read & write" rather
* than two separate lines.
*/
function summarizeGrantedScopes(scopes) {
	const granted = new Set(scopes);
	const summaries = [];
	for (const group of PUBLIC_API_SCOPE_GROUPS) {
		const hasWrite = group.write !== null && granted.has(group.write);
		const hasRead = group.read !== null && granted.has(group.read);
		if (hasWrite) summaries.push({
			key: group.key,
			label: group.label,
			level: "write",
			summary: group.writeSummary
		});
		else if (hasRead) summaries.push({
			key: group.key,
			label: group.label,
			level: "read",
			summary: group.readSummary
		});
	}
	return summaries;
}

//#endregion
//#region src/coupon-campaigns.ts
/**
* Lifecycle of a seller coupon campaign. `pending` waits for CardNexus to
* review it and `needs_info` for the seller to answer a staff question; the
* reply returns it to `pending`. `approved` campaigns have their codes;
* `declined`, `withdrawn` and `stopped` are final.
*/
const COUPON_CAMPAIGN_STATUSES = [
	"pending",
	"needs_info",
	"approved",
	"declined",
	"withdrawn",
	"stopped"
];
/**
* Statuses of a request CardNexus has not decided yet: the seller may still
* edit or withdraw it.
*/
const COUPON_CAMPAIGN_OPEN_STATUSES = ["pending", "needs_info"];
/** Whether a campaign in `status` is still an open request. */
function isOpenCouponCampaignStatus(status) {
	return COUPON_CAMPAIGN_OPEN_STATUSES.includes(status);
}
/** Who wrote a message on a campaign: CardNexus staff, or the seller. */
const COUPON_CAMPAIGN_MESSAGE_AUTHOR_TYPES = ["staff", "seller"];
/**
* Bounds of a campaign request. Code values are cents of the shop's
* currency; `maxCodes` caps the sum of the lines' quantities.
*/
const COUPON_CAMPAIGN_LIMITS = {
	maxCodes: 2e3,
	maxLines: 10,
	minCodeValue: 100,
	maxCodeValue: 5e4,
	prefixMaxLength: 12,
	nameMaxLength: 80,
	purposeMaxLength: 1e3,
	messageMaxLength: 2e3,
	maxDurationDays: 366
};
/** Capital letters and digits, the prefix of every code of a campaign (`PREFIX-XXXXXXXX`). */
const COUPON_CAMPAIGN_CODE_PREFIX_REGEX = new RegExp(`^[A-Z0-9]{2,${COUPON_CAMPAIGN_LIMITS.prefixMaxLength}}$`);
/**
* The shop's share, in basis points, at which it pays every code in full:
* the campaign needs no review and is approved on creation.
*/
const COUPON_CAMPAIGN_FULL_SHOP_SHARE_BPS = 1e4;
/**
* Time zone of a campaign's days: codes work from 00:00 on the first day to
* 00:00 on the day after the last day, in the zone of the shop's region.
*/
const COUPON_CAMPAIGN_TIME_ZONES = {
	EU: "Europe/Paris",
	NA: "America/New_York"
};
/** Where an approved campaign stands in time. */
const COUPON_CAMPAIGN_PHASES = [
	"scheduled",
	"live",
	"ended"
];
/**
* Where an approved campaign stands at `now`: `scheduled` before `startsAt`,
* `live` from `startsAt` until just before `endsAt`, `ended` from `endsAt`.
*/
function couponCampaignPhase(campaign, now) {
	if (now.getTime() < campaign.startsAt.getTime()) return "scheduled";
	if (now.getTime() < campaign.endsAt.getTime()) return "live";
	return "ended";
}
/**
* The status a seller and the admin see: the stored status, with `approved`
* replaced by the campaign's phase.
*/
const COUPON_CAMPAIGN_DISPLAY_STATUSES = [
	"pending",
	"needs_info",
	"scheduled",
	"live",
	"ended",
	"declined",
	"withdrawn",
	"stopped"
];
/** The status to show for a campaign at `now`; see `COUPON_CAMPAIGN_DISPLAY_STATUSES`. */
function couponCampaignDisplayStatus(campaign, now) {
	if (campaign.status === "approved") return couponCampaignPhase(campaign, now);
	return campaign.status;
}

//#endregion
//#region src/sealed-product-types.ts
/**
* The sealed product types every game shares. A product carries exactly one of
* these values as its category; which ones a game uses depends on its catalogue.
*/
let SealedProductType = /* @__PURE__ */ function(SealedProductType) {
	SealedProductType["BOOSTER"] = "booster";
	SealedProductType["COLLECTOR_BOOSTER"] = "collector_booster";
	SealedProductType["PROMO_PACK"] = "promo_pack";
	SealedProductType["BOOSTER_BOX"] = "booster_box";
	SealedProductType["COLLECTOR_BOOSTER_BOX"] = "collector_booster_box";
	SealedProductType["BOOSTER_BOX_CASE"] = "booster_box_case";
	SealedProductType["ETB_CASE"] = "etb_case";
	SealedProductType["BUNDLE_CASE"] = "bundle_case";
	SealedProductType["DECK_CASE"] = "deck_case";
	SealedProductType["COLLECTION_CASE"] = "collection_case";
	SealedProductType["PRERELEASE_CASE"] = "prerelease_case";
	SealedProductType["TIN_CASE"] = "tin_case";
	SealedProductType["BLISTER_CASE"] = "blister_case";
	SealedProductType["BUNDLE"] = "bundle";
	SealedProductType["BOOSTER_BUNDLE"] = "booster_bundle";
	SealedProductType["ILLUMINEERS_TROVE"] = "illumineers_trove";
	SealedProductType["ELITE_TRAINER_BOX"] = "elite_trainer_box";
	SealedProductType["BOX_SET"] = "box_set";
	SealedProductType["PREMIUM_COLLECTION"] = "premium_collection";
	SealedProductType["EX_BOX"] = "ex_box";
	SealedProductType["BUILD_AND_BATTLE"] = "build_and_battle";
	SealedProductType["SECRET_LAIR"] = "secret_lair";
	SealedProductType["TIN"] = "tin";
	SealedProductType["MINI_TIN"] = "mini_tin";
	SealedProductType["BLISTER"] = "blister";
	SealedProductType["DECK"] = "deck";
	SealedProductType["STARTER_DECK"] = "starter_deck";
	SealedProductType["THEME_DECK"] = "theme_deck";
	SealedProductType["COMMANDER_DECK"] = "commander_deck";
	SealedProductType["DECK_DISPLAY"] = "deck_display";
	SealedProductType["PRERELEASE_KIT"] = "prerelease_kit";
	SealedProductType["KIT"] = "kit";
	SealedProductType["GIFT_SET"] = "gift_set";
	SealedProductType["TRAINER_KIT"] = "trainer_kit";
	SealedProductType["REDEMPTION_BOX"] = "redemption_box";
	SealedProductType["OTHER"] = "other";
	return SealedProductType;
}({});
/**
* A broader grouping of sealed product types. It is never stored: it is derived
* from the product type and drives the parcel size and the broader catalogue
* pages (a family's main page lists every type in the family).
*/
let SealedProductFamily = /* @__PURE__ */ function(SealedProductFamily) {
	SealedProductFamily["BOOSTER"] = "booster";
	SealedProductFamily["BOOSTER_BOX"] = "booster_box";
	SealedProductFamily["CASE"] = "case";
	SealedProductFamily["BUNDLE"] = "bundle";
	SealedProductFamily["ELITE_TRAINER_BOX"] = "elite_trainer_box";
	SealedProductFamily["BOX_SET"] = "box_set";
	SealedProductFamily["TIN"] = "tin";
	SealedProductFamily["BLISTER"] = "blister";
	SealedProductFamily["DECK"] = "deck";
	SealedProductFamily["DECK_DISPLAY"] = "deck_display";
	SealedProductFamily["KIT"] = "kit";
	SealedProductFamily["GIFT_SET"] = "gift_set";
	SealedProductFamily["TRAINER_KIT"] = "trainer_kit";
	SealedProductFamily["REDEMPTION_BOX"] = "redemption_box";
	SealedProductFamily["OTHER"] = "other";
	return SealedProductFamily;
}({});
/** The family each sealed product type belongs to. */
const SEALED_PRODUCT_TYPE_FAMILY = {
	[SealedProductType.BOOSTER]: SealedProductFamily.BOOSTER,
	[SealedProductType.COLLECTOR_BOOSTER]: SealedProductFamily.BOOSTER,
	[SealedProductType.PROMO_PACK]: SealedProductFamily.BOOSTER,
	[SealedProductType.BOOSTER_BOX]: SealedProductFamily.BOOSTER_BOX,
	[SealedProductType.COLLECTOR_BOOSTER_BOX]: SealedProductFamily.BOOSTER_BOX,
	[SealedProductType.BOOSTER_BOX_CASE]: SealedProductFamily.CASE,
	[SealedProductType.ETB_CASE]: SealedProductFamily.CASE,
	[SealedProductType.BUNDLE_CASE]: SealedProductFamily.CASE,
	[SealedProductType.DECK_CASE]: SealedProductFamily.CASE,
	[SealedProductType.COLLECTION_CASE]: SealedProductFamily.CASE,
	[SealedProductType.PRERELEASE_CASE]: SealedProductFamily.CASE,
	[SealedProductType.TIN_CASE]: SealedProductFamily.CASE,
	[SealedProductType.BLISTER_CASE]: SealedProductFamily.CASE,
	[SealedProductType.BUNDLE]: SealedProductFamily.BUNDLE,
	[SealedProductType.BOOSTER_BUNDLE]: SealedProductFamily.BUNDLE,
	[SealedProductType.ILLUMINEERS_TROVE]: SealedProductFamily.BUNDLE,
	[SealedProductType.ELITE_TRAINER_BOX]: SealedProductFamily.ELITE_TRAINER_BOX,
	[SealedProductType.BOX_SET]: SealedProductFamily.BOX_SET,
	[SealedProductType.PREMIUM_COLLECTION]: SealedProductFamily.BOX_SET,
	[SealedProductType.EX_BOX]: SealedProductFamily.BOX_SET,
	[SealedProductType.BUILD_AND_BATTLE]: SealedProductFamily.BOX_SET,
	[SealedProductType.SECRET_LAIR]: SealedProductFamily.BOX_SET,
	[SealedProductType.TIN]: SealedProductFamily.TIN,
	[SealedProductType.MINI_TIN]: SealedProductFamily.TIN,
	[SealedProductType.BLISTER]: SealedProductFamily.BLISTER,
	[SealedProductType.DECK]: SealedProductFamily.DECK,
	[SealedProductType.STARTER_DECK]: SealedProductFamily.DECK,
	[SealedProductType.THEME_DECK]: SealedProductFamily.DECK,
	[SealedProductType.COMMANDER_DECK]: SealedProductFamily.DECK,
	[SealedProductType.DECK_DISPLAY]: SealedProductFamily.DECK_DISPLAY,
	[SealedProductType.PRERELEASE_KIT]: SealedProductFamily.KIT,
	[SealedProductType.KIT]: SealedProductFamily.KIT,
	[SealedProductType.GIFT_SET]: SealedProductFamily.GIFT_SET,
	[SealedProductType.TRAINER_KIT]: SealedProductFamily.TRAINER_KIT,
	[SealedProductType.REDEMPTION_BOX]: SealedProductFamily.REDEMPTION_BOX,
	[SealedProductType.OTHER]: SealedProductFamily.OTHER
};
const SEALED_PRODUCT_TYPE_VALUES = new Set(Object.values(SealedProductType));
/** True when the value is one of the shared sealed product types (not a retired per-game value). */
function isSealedProductType(value) {
	return SEALED_PRODUCT_TYPE_VALUES.has(value);
}
/** The family of a sealed product type; undefined for a retired per-game value. */
function sealedProductFamily(value) {
	return isSealedProductType(value) ? SEALED_PRODUCT_TYPE_FAMILY[value] : void 0;
}
/** Every sealed product type in a family. */
function sealedProductTypesInFamily(family) {
	return Object.values(SealedProductType).filter((type) => SEALED_PRODUCT_TYPE_FAMILY[type] === family);
}

//#endregion
//#region src/user-flags.ts
/**
* Admin-managed per-user flags, distinct from Amplitude Experiment flags.
*
* The Postgres `user_flag` enum is generated from this array. Append new flags:
* reordering makes drizzle recreate the type instead of emitting a cheap
* `ALTER TYPE ... ADD VALUE`. Removing a flag also recreates the type, so its
* migration must delete the flag's `user_flags` rows first.
*/
const USER_FLAGS = [
	"public-api",
	"sendcloud-shipping",
	"untracked-shipping-disabled",
	"mcp"
];
/**
* Named flags, so call sites reference a constant instead of repeating the
* string. `satisfies` keeps every value a real member of {@link USER_FLAGS}.
*/
const USER_FLAG = {
	PUBLIC_API: "public-api",
	SENDCLOUD_SHIPPING: "sendcloud-shipping",
	UNTRACKED_SHIPPING_DISABLED: "untracked-shipping-disabled"
};

//#endregion
//#region src/shipping.ts
/**
* Shipping services a buyer can be charged for on one seller's sub-order.
* - tracked: a tracked shipment (the default everywhere)
* - untracked: a plain or IMb-barcoded letter, offered on small orders only
*/
const SHIPPING_SERVICES = ["tracked", "untracked"];
/** Package sizes that can ship as an untracked letter. */
const UNTRACKED_PACKAGE_SIZES = ["xsmall", "small"];
/** Shipping zones where a seller may offer untracked letters. */
const UNTRACKED_ZONES = ["domestic", "region"];
/**
* European countries for region mapping.
* Includes EU members plus UK, Switzerland, and Norway.
*/
const EU_COUNTRIES = [
	"AT",
	"BE",
	"CH",
	"CY",
	"DE",
	"DK",
	"EE",
	"ES",
	"FI",
	"FR",
	"GB",
	"GR",
	"HR",
	"HU",
	"IE",
	"IT",
	"LT",
	"LU",
	"LV",
	"MT",
	"NL",
	"NO",
	"PL",
	"PT",
	"SE",
	"SI",
	"SK"
];
/**
* North American countries for region mapping.
*/
const NA_COUNTRIES = ["US", "CA"];
/**
* Countries where SendCloud shipping is activated: the buyer pays the
* CardNexus-owned country grid and the seller fulfils via SendCloud labels.
*
* It is constrained at compile time to {@link EU_COUNTRIES} (the `satisfies`
* below), so a NA country can never be activated by mistake.
*/
const SENDCLOUD_ACTIVATED_COUNTRIES = ["FR"];
const ACTIVATED_SET = new Set(SENDCLOUD_ACTIVATED_COUNTRIES);
/**
* Whether a seller's country is SendCloud-activated. Case-insensitive on the
* ISO-2 code. This is the *country* gate only — the full activation gate ANDs it
* with the seller's `sendcloud-shipping` user flag, so that within an activated
* country SendCloud is enabled seller by seller.
*/
function isSendCloudActivatedCountry(country) {
	if (!country) return false;
	return ACTIVATED_SET.has(country.toUpperCase());
}
/**
* - `managed`: CardNexus prices the shipping and provides a prepaid label.
* - `own_rates`: the seller prices shipping with their own rate grid.
*/
const SELLER_SHIPPING_MODES = ["managed", "own_rates"];
/**
* The full SendCloud activation gate: the seller's `sendcloud-shipping` flag is
* on AND their country is activated. When true, CardNexus owns buyer shipping
* for this seller, so the seller rate grid does not apply to them.
*
* @param flags - the seller's enabled user flags
* @param country - the seller's ISO-2 country code
*/
function isShippingManagedByCardNexus(flags, country) {
	return (flags ?? []).includes(USER_FLAG.SENDCLOUD_SHIPPING) && isSendCloudActivatedCountry(country);
}
/**
* Maximum allowed custom shipping rates per currency (in cents). Hard caps to
* prevent abuse. Strongly typed: adding a currency to
* SupportedMarketplaceCurrency errors here until its caps are added.
*/
const MAX_RATES = {
	[SupportedMarketplaceCurrency.EUR]: {
		xsmall: 3e3,
		small: 3e3,
		medium: 7500,
		large: 1e4,
		xlarge: 15e3
	},
	[SupportedMarketplaceCurrency.USD]: {
		xsmall: 4200,
		small: 4200,
		medium: 6750,
		large: 11500,
		xlarge: 17e3
	},
	[SupportedMarketplaceCurrency.GBP]: {
		xsmall: 2200,
		small: 2200,
		medium: 5500,
		large: 9e3,
		xlarge: 13500
	},
	[SupportedMarketplaceCurrency.CAD]: {
		xsmall: 5500,
		small: 5500,
		medium: 9e3,
		large: 15e3,
		xlarge: 22500
	},
	[SupportedMarketplaceCurrency.CHF]: {
		xsmall: 2500,
		small: 2500,
		medium: 6e3,
		large: 1e4,
		xlarge: 15e3
	},
	[SupportedMarketplaceCurrency.SEK]: {
		xsmall: 33e3,
		small: 33e3,
		medium: 82500,
		large: 11e4,
		xlarge: 165e3
	},
	[SupportedMarketplaceCurrency.DKK]: {
		xsmall: 3e4,
		small: 3e4,
		medium: 56e3,
		large: 75e3,
		xlarge: 112e3
	},
	[SupportedMarketplaceCurrency.NOK]: {
		xsmall: 35e3,
		small: 35e3,
		medium: 87500,
		large: 117e3,
		xlarge: 175e3
	},
	[SupportedMarketplaceCurrency.PLN]: {
		xsmall: 13e3,
		small: 13e3,
		medium: 32500,
		large: 43e3,
		xlarge: 65e3
	},
	[SupportedMarketplaceCurrency.HUF]: {
		xsmall: 12e5,
		small: 12e5,
		medium: 3e6,
		large: 4e6,
		xlarge: 6e6
	}
};
function untrackedCaps(rates) {
	return {
		xsmall: rates.xsmall,
		small: rates.small
	};
}
/**
* Maximum allowed untracked letter rates per currency (in cents): the
* {@link MAX_RATES} caps of the untracked package sizes.
*/
const MAX_UNTRACKED_RATES = {
	[SupportedMarketplaceCurrency.EUR]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.EUR]),
	[SupportedMarketplaceCurrency.USD]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.USD]),
	[SupportedMarketplaceCurrency.GBP]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.GBP]),
	[SupportedMarketplaceCurrency.CAD]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.CAD]),
	[SupportedMarketplaceCurrency.CHF]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.CHF]),
	[SupportedMarketplaceCurrency.SEK]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.SEK]),
	[SupportedMarketplaceCurrency.DKK]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.DKK]),
	[SupportedMarketplaceCurrency.NOK]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.NOK]),
	[SupportedMarketplaceCurrency.PLN]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.PLN]),
	[SupportedMarketplaceCurrency.HUF]: untrackedCaps(MAX_RATES[SupportedMarketplaceCurrency.HUF])
};
/** All valid package sizes in order from smallest to largest. */
const PACKAGE_SIZES = [
	"xsmall",
	"small",
	"medium",
	"large",
	"xlarge"
];
/** Sealed families that ship as a box: a display, bundle, ETB, box set, tin, gift set. */
const LARGE_SEALED_FAMILIES = new Set([
	SealedProductFamily.BOOSTER_BOX,
	SealedProductFamily.BUNDLE,
	SealedProductFamily.ELITE_TRAINER_BOX,
	SealedProductFamily.BOX_SET,
	SealedProductFamily.TIN,
	SealedProductFamily.DECK_DISPLAY,
	SealedProductFamily.GIFT_SET,
	SealedProductFamily.REDEMPTION_BOX
]);
/** Retired per-game values that still ship large (products and orders not yet migrated). */
const LEGACY_LARGE_SEALED_CATEGORIES = [
	"deck_box",
	"preconstructed_deck_box",
	"box_sets",
	"multiple_deck"
];
const LARGE_SEALED_CATEGORIES = [...Object.values(SealedProductType).filter((type) => LARGE_SEALED_FAMILIES.has(SEALED_PRODUCT_TYPE_FAMILY[type])), ...LEGACY_LARGE_SEALED_CATEGORIES];
const PACKAGE_SIZE_DEFINITIONS = {
	xsmall: {
		shortLabel: "XS",
		minCards: 1,
		maxCards: 3,
		sweepMaxCards: 3,
		sealedCategories: [],
		sealedFallback: false,
		contents: "1-3 cards"
	},
	small: {
		shortLabel: "S",
		minCards: 4,
		maxCards: 25,
		sweepMaxCards: 25,
		sealedCategories: [],
		sealedFallback: false,
		contents: "4-25 cards"
	},
	medium: {
		shortLabel: "M",
		minCards: 26,
		maxCards: 250,
		sweepMaxCards: 250,
		sealedCategories: [],
		sealedFallback: true,
		contents: "26-250 cards, boosters, blisters, sealed decks"
	},
	large: {
		shortLabel: "L",
		minCards: 251,
		maxCards: null,
		sweepMaxCards: 500,
		sealedCategories: LARGE_SEALED_CATEGORIES,
		sealedFallback: false,
		contents: "251+ cards, displays, bundles, deck boxes, ETB, tins, gift sets"
	},
	xlarge: {
		shortLabel: "XL",
		minCards: null,
		maxCards: null,
		sweepMaxCards: null,
		sealedCategories: [],
		sealedFallback: false,
		contents: "cases"
	}
};
/** The size a sealed category falls to when it matches no other rule. */
const SEALED_FALLBACK_SIZE = PACKAGE_SIZES.find((size) => PACKAGE_SIZE_DEFINITIONS[size].sealedFallback) ?? "medium";
/** Substring identifying a sealed case category (booster_case, bundle_case, …). */
const SEALED_CASE_MARKER = "case";
/** All valid shipping zones in order from narrowest to broadest. */
const SHIPPING_ZONES = [
	"domestic",
	"region",
	"global"
];
/**
* Determines the shipping zone between seller and buyer countries.
*/
function getShippingZone(sellerCountry, buyerCountry) {
	if (sellerCountry === buyerCountry) return "domestic";
	const sellerIsEU = EU_COUNTRIES.includes(sellerCountry);
	const buyerIsEU = EU_COUNTRIES.includes(buyerCountry);
	if (sellerIsEU && buyerIsEU) return "region";
	const sellerIsNA = NA_COUNTRIES.includes(sellerCountry);
	const buyerIsNA = NA_COUNTRIES.includes(buyerCountry);
	if (sellerIsNA && buyerIsNA) return "region";
	return "global";
}
/**
* Gets all countries in a seller's "region" zone (excluding their own country).
*/
function getRegionCountries(sellerCountry) {
	if (EU_COUNTRIES.includes(sellerCountry)) return EU_COUNTRIES.filter((c) => c !== sellerCountry);
	if (NA_COUNTRIES.includes(sellerCountry)) return NA_COUNTRIES.filter((c) => c !== sellerCountry);
	return [];
}
/**
* Returns a region identifier for a country, or null if it has no known region.
*/
function getRegionKey(country) {
	if (EU_COUNTRIES.includes(country)) return "europe";
	if (NA_COUNTRIES.includes(country)) return "northAmerica";
	return null;
}
/**
* Whether a seller can ship to a buyer's country given the seller's enabled
* shipping zones.
*/
function canShipTo(sellerCountry, buyerCountry, enabledZones) {
	return enabledZones[getShippingZone(sellerCountry, buyerCountry)];
}
/**
* Computes the shipsTo array from a seller's enabled zones.
* Zone hierarchy: domestic → region → global (each requires the previous).
*/
function computeShipsTo(sellerCountry, enabledZones) {
	if (!enabledZones.domestic) return [];
	if (enabledZones.global) return Array.from(new Set([
		sellerCountry,
		...EU_COUNTRIES,
		...NA_COUNTRIES
	]));
	const countries = [sellerCountry];
	if (enabledZones.region) countries.push(...getRegionCountries(sellerCountry));
	return countries;
}

//#endregion
//#region src/stripe.ts
/**
* Stripe Customer Tax ID types for pro seller fee invoices.
* All values are valid for stripe.customers.createTaxId({ type, value }).
*/
const StripeCustomerTaxIdType = {
	EU_VAT: "eu_vat",
	GB_VAT: "gb_vat",
	CH_VAT: "ch_vat",
	NO_VAT: "no_vat",
	LI_VAT: "li_vat",
	DE_STN: "de_stn",
	ES_CIF: "es_cif",
	HR_OIB: "hr_oib",
	BG_UIC: "bg_uic",
	HU_TIN: "hu_tin",
	IT_CF: "it_cf",
	PL_NIP: "pl_nip",
	RO_TIN: "ro_tin",
	SI_TIN: "si_tin",
	CH_UID: "ch_uid",
	LI_UID: "li_uid",
	GI_TIN: "gi_tin",
	US_EIN: "us_ein",
	CA_BN: "ca_bn",
	CA_GST_HST: "ca_gst_hst"
};
const STRIPE_CUSTOMER_TAX_ID_TYPES = Object.values(StripeCustomerTaxIdType);
/** Countries where the registrationNumber maps to a Stripe Customer Tax ID. */
const REGISTRATION_STRIPE_TYPE = {
	BG: StripeCustomerTaxIdType.BG_UIC,
	CH: StripeCustomerTaxIdType.CH_UID,
	HR: StripeCustomerTaxIdType.HR_OIB,
	LI: StripeCustomerTaxIdType.LI_UID
};
const STRIPE_ONBOARDING_STATUSES = [
	"not_started",
	"pending",
	"complete",
	"restricted"
];

//#endregion
//#region src/vat.ts
/**
* Version stamped on every VAT classification the checkout produces
* (`transactions.vat_classification.version`, `orders.vat.classificationVersion`).
* Bump it whenever a rule in the resolver changes so the ledger can tell
* which rule set produced a stored snapshot.
*/
const VAT_CLASSIFICATION_VERSION = 2;
/** IOSS: goods intrinsic value at or below this amount (EUR cents) makes CardNexus the deemed supplier. */
const IOSS_THRESHOLD_EUR_CENTS = 15e3;
/** UK OMP: goods intrinsic value at or below this amount (GBP cents) makes CardNexus the deemed supplier. */
const UK_OMP_THRESHOLD_GBP_CENTS = 13500;
/**
* Filing schemes a VAT line can belong to. `reverse_charge` and
* `out_of_scope` carry zero VAT and exist for completeness checks.
*/
const VAT_SCHEMES = [
	"fr_domestic",
	"eu_oss",
	"ioss",
	"uk_vat",
	"uk_omp",
	"ch_vat",
	"reverse_charge",
	"out_of_scope"
];
/** What a VAT line taxes: the goods themselves or one of the two platform fees. */
const VAT_BASES = [
	"goods",
	"buyer_fee",
	"seller_fee"
];
/** Event that produces a VAT line. */
const VAT_LINE_EVENT_KINDS = [
	"charge",
	"refund",
	"payout",
	"invoice",
	"adjustment"
];
/** Where the rate used by a classification came from. */
const VAT_RATE_SOURCES = ["live", "static_fallback"];
/**
* How a fee-paying party is treated for fee VAT.
* - consumer: VAT due in the party's country (FR domestic, OSS, UK, CH)
* - pro_without_vat_number: EU professional with no VAT number, treated as a consumer (D8)
* - fr_pro_invoice: French professional; VAT is on the monthly Stripe invoice
* - reverse_charge: EU professional with a VAT number
* - out_of_scope: third-country party (no CardNexus obligation, or a registration to confirm)
* - none: not an EU-entity fee (NA region)
*/
const FEE_PARTY_TREATMENTS = [
	"consumer",
	"pro_without_vat_number",
	"fr_pro_invoice",
	"reverse_charge",
	"out_of_scope",
	"none"
];
/** Flags a classification can raise for the declaration pack. */
const VAT_CLASSIFICATION_FLAGS = ["no_voec_to_confirm", "registration_pending"];
function roundHalfAwayFromZero(value) {
	return Math.sign(value) * Math.round(Math.abs(value));
}
/**
* Splits a VAT-inclusive amount into its base and VAT parts:
* `vat = round(gross × r / (1 + r))`, `base = gross − vat`.
* Negative amounts (refunds) round symmetrically so a refund of a charge
* yields the exact opposite split. `base + vat` always equals `gross`.
*/
function splitInclusiveVat(grossCents, rate) {
	const vat = roundHalfAwayFromZero(grossCents * rate / (1 + rate));
	return {
		base: grossCents - vat,
		vat
	};
}
/**
* VAT added on top of a VAT-exclusive base: `round(base × r)`, rounded
* symmetrically for negative amounts.
*/
function exclusiveVat(baseCents, rate) {
	return roundHalfAwayFromZero(baseCents * rate);
}
/**
* IOSS / UK OMP facilitator VAT mechanism.
* Determines which VAT collection regime CardNexus applies as a marketplace facilitator.
* - ioss:   EU IOSS — non-EU seller → EU individual buyer → goods ≤€150
* - uk_omp: UK OMP — non-UK seller → UK individual buyer → goods ≤£135
*/
const FACILITATOR_MECHANISMS = ["ioss", "uk_omp"];
/**
* VAT mechanism applied to CardNexus commission fees (seller fee and buyer fee).
* Determines which declaration pathway CardNexus uses for that fee obligation.
*
* - fr_domestic: French 20% TVA — billed via monthly Stripe invoice with frenchTaxRateId
* - eu_oss:      EU non-FR individual — declared quarterly via OSS UE at impots.gouv.fr
* - uk_vat:      UK VAT (20%) — requires separate UK VAT registration and return
* - ch_vat:      Swiss MWST (8.1%) — requires separate Swiss VAT registration
* - ca_gst:      Canadian GST/HST — requires separate Canadian registration (future)
*
* Absent = no obligation (PRO/B2B → reverse charge, non-EU without local VAT rule, NA).
*/
const FEE_VAT_TYPES = [
	"fr_domestic",
	"eu_oss",
	"uk_vat",
	"ch_vat",
	"ca_gst"
];
/**
* Standard VAT rates per EU destination country for IOSS calculations.
* Source: EC TEDB SOAP service. Verified April 2026.
*/
const EU_IOSS_VAT_RATES = {
	AT: .2,
	BE: .21,
	BG: .2,
	HR: .25,
	CY: .19,
	CZ: .21,
	DK: .25,
	EE: .24,
	FI: .255,
	FR: .2,
	DE: .19,
	GR: .24,
	HU: .27,
	IE: .23,
	IT: .22,
	LV: .21,
	LT: .21,
	LU: .17,
	MT: .18,
	NL: .21,
	PL: .23,
	PT: .23,
	RO: .21,
	SK: .23,
	SI: .22,
	ES: .21,
	SE: .25
};
/**
* EU VAT member states — derived from EU_IOSS_VAT_RATES keys (EU-27).
* Excludes GB, CH, NO, LI — not EU VAT territory.
*/
const EU_VAT_MEMBER_STATES = new Set(Object.keys(EU_IOSS_VAT_RATES));
/**
* Standard VAT rates of the non-EU European countries CardNexus operates in.
* No live source exists for them (no TEDB equivalent), so this table is the
* origin of their `vat_rates` rows.
*
* Sources (manually verified):
*   GB: UK HMRC — 20% since 2011
*   CH: Swiss ESTV (MWST) — 8.1% since Jan 2024 (raised from 7.7%)
*   NO: Norwegian Skatteetaten (MVA) — 25%
*/
const NON_EU_VAT_RATES = {
	GB: .2,
	CH: .081,
	NO: .25
};
/** Every static rate the engine can fall back on when the `vat_rates` table is unreachable. */
const STATIC_VAT_RATES = {
	...EU_IOSS_VAT_RATES,
	...NON_EU_VAT_RATES
};
const UK_COUNTRIES = new Set(["GB"]);
/**
* The deemed-supplier regime of a consumer in `buyerCountry`, with the seller
* countries it leaves out because their sellers handle that VAT themselves.
* `null` outside the EU and the UK.
*/
function facilitatorRegime(buyerCountry) {
	if (EU_VAT_MEMBER_STATES.has(buyerCountry)) return {
		mechanism: "ioss",
		exemptSellerCountries: EU_VAT_MEMBER_STATES
	};
	if (buyerCountry === "GB") return {
		mechanism: "uk_omp",
		exemptSellerCountries: UK_COUNTRIES
	};
	return null;
}
/**
* Deemed-supplier regime the trade flow qualifies for, before the goods value
* threshold is looked at. `null` when CardNexus can never be the deemed
* supplier for this seller → buyer pair.
*
* Per Racine Avocats TVA note (April 2026, section 4.1 and Tableau de synthèse p.14-15):
* - Individual (non-pro) sellers → CardNexus NOT liable regardless of buyer or value
* - B2B transactions → no IOSS (reverse charge by buyer)
* - EU seller → seller handles own VAT (intra-EU rules apply)
*/
function facilitatorCandidate(flow) {
	if (!flow.sellerIsPro) return null;
	if (flow.buyerIsB2B) return null;
	const regime = facilitatorRegime(flow.buyerCountry);
	if (!regime || regime.exemptSellerCountries.has(flow.sellerCountry)) return null;
	return regime.mechanism;
}
const FACILITATOR_THRESHOLDS = {
	ioss: {
		currency: "EUR",
		cents: IOSS_THRESHOLD_EUR_CENTS
	},
	uk_omp: {
		currency: "GBP",
		cents: UK_OMP_THRESHOLD_GBP_CENTS
	}
};
/**
* Whether goods worth `valueCents`, in minor units of the mechanism's
* threshold currency, are within its threshold: CardNexus is the deemed
* supplier at or below it.
*/
function withinFacilitatorThreshold(mechanism, valueCents) {
	return valueCents <= FACILITATOR_THRESHOLDS[mechanism].cents;
}
/**
* Country whose standard rate applies to the goods under a mechanism: the
* destination for IOSS, the UK for UK OMP.
*/
function facilitatorRateCountry(mechanism, buyerCountry) {
	return mechanism === "ioss" ? buyerCountry : "GB";
}
/** Stripe tax ID types that represent VAT registrations. */
const VAT_TAX_ID_TYPES = new Set([
	"eu_vat",
	"gb_vat",
	"ch_vat",
	"no_vat",
	"li_vat",
	"ca_gst_hst"
]);
const TAX_ID_TYPE_SET = new Set(STRIPE_CUSTOMER_TAX_ID_TYPES);
/**
* Narrow a user-supplied string to a valid StripeCustomerTaxIdType,
* or undefined if the value isn't recognized. Use at form->API boundaries
* to prevent shipping invalid identifier types to Stripe.
*/
const toStripeTaxIdType = (value) => value && TAX_ID_TYPE_SET.has(value) ? value : void 0;
const COUNTRY_CONFIG = {
	AT: {
		registration: {
			example: "FN 123456 a",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "ATU12345678"
		}
	},
	BE: {
		registration: {
			example: "0123.456.789",
			required: true
		},
		vat: {
			type: "eu_vat",
			example: "BE0123456789"
		}
	},
	BG: {
		registration: {
			example: "123456789",
			required: true,
			stripeType: "bg_uic"
		},
		vat: {
			type: "eu_vat",
			example: "BG0123456789"
		}
	},
	CY: {
		registration: {
			example: "HE 123456",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "CY12345678Z"
		}
	},
	CZ: {
		registration: {
			example: "12345678",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "CZ1234567890"
		}
	},
	DE: {
		registration: {
			example: "HRB 12345",
			required: false
		},
		taxId: [{
			type: "de_stn",
			example: "1234567890"
		}],
		vat: {
			type: "eu_vat",
			example: "DE123456789"
		}
	},
	DK: {
		registration: {
			example: "12345678",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "DK12345678"
		}
	},
	EE: {
		registration: {
			example: "12345678",
			required: true
		},
		vat: {
			type: "eu_vat",
			example: "EE123456789"
		}
	},
	ES: {
		registration: {
			example: "A12345678",
			required: false
		},
		taxId: [{
			type: "es_cif",
			example: "A12345678"
		}],
		vat: {
			type: "eu_vat",
			example: "ESA1234567Z"
		}
	},
	FI: {
		registration: {
			example: "1234567-8",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "FI12345678"
		}
	},
	FR: {
		registration: {
			example: "123456789",
			required: true
		},
		vat: {
			type: "eu_vat",
			example: "FR12345678901"
		}
	},
	GR: {
		registration: {
			example: "123456789000",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "EL123456789"
		}
	},
	HR: {
		registration: {
			example: "080123456",
			required: false,
			stripeType: "hr_oib"
		},
		vat: {
			type: "eu_vat",
			example: "HR12345678912"
		}
	},
	HU: {
		registration: {
			example: "01-09-123456",
			required: true
		},
		taxId: [{
			type: "hu_tin",
			example: "12345678-1-23"
		}],
		vat: {
			type: "eu_vat",
			example: "HU12345678"
		}
	},
	IE: {
		registration: {
			example: "123456",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "IE1234567AB"
		}
	},
	IT: {
		registration: {
			example: "MI-1234567",
			required: false
		},
		taxId: [{
			type: "it_cf",
			example: "ABCDEF12A12A123A"
		}],
		vat: {
			type: "eu_vat",
			example: "IT12345678912"
		}
	},
	LT: {
		registration: {
			example: "123456789",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "LT123456789123"
		}
	},
	LU: {
		registration: {
			example: "B123456",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "LU12345678"
		}
	},
	LV: {
		registration: {
			example: "40003123456",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "LV12345678912"
		}
	},
	MT: {
		registration: {
			example: "C 12345",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "MT12345678"
		}
	},
	NL: {
		registration: {
			example: "12345678",
			required: true
		},
		vat: {
			type: "eu_vat",
			example: "NL123456789B12"
		}
	},
	PL: {
		registration: {
			example: "0000123456",
			required: false
		},
		taxId: [{
			type: "pl_nip",
			example: "1234567890"
		}],
		vat: {
			type: "eu_vat",
			example: "PL1234567890"
		}
	},
	PT: {
		registration: {
			example: "123456789",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "PT123456789"
		}
	},
	RO: {
		registration: {
			example: "RO12345678",
			required: false
		},
		taxId: [{
			type: "ro_tin",
			example: "1234567890123"
		}],
		vat: {
			type: "eu_vat",
			example: "RO1234567891"
		}
	},
	SE: {
		registration: {
			example: "556123-4567",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "SE123456789123"
		}
	},
	SI: {
		registration: {
			example: "1234567000",
			required: true
		},
		taxId: [{
			type: "si_tin",
			example: "12345678"
		}],
		vat: {
			type: "eu_vat",
			example: "SI12345678"
		}
	},
	SK: {
		registration: {
			example: "12345678",
			required: false
		},
		vat: {
			type: "eu_vat",
			example: "SK1234567891"
		}
	},
	GB: {
		registration: {
			example: "01234567",
			required: false
		},
		vat: {
			type: "gb_vat",
			example: "GB123456789"
		}
	},
	CH: {
		registration: {
			example: "CHE-123.456.789",
			required: false,
			stripeType: "ch_uid"
		},
		vat: {
			type: "ch_vat",
			example: "CHE-123.456.789 MWST"
		}
	},
	NO: {
		registration: {
			example: "123456789",
			required: false
		},
		vat: {
			type: "no_vat",
			example: "123456789MVA"
		}
	},
	LI: {
		registration: {
			example: "CHE123456789",
			required: false,
			stripeType: "li_uid"
		},
		vat: {
			type: "li_vat",
			example: "12345"
		}
	},
	GI: { taxId: [{
		type: "gi_tin",
		example: "12345"
	}] },
	US: { taxId: [{
		type: "us_ein",
		example: "12-3456789"
	}] },
	CA: {
		taxId: [{
			type: "ca_bn",
			example: "123456789"
		}],
		vat: {
			type: "ca_gst_hst",
			example: "123456789RT0001"
		}
	}
};
function getCountryConfig(country) {
	return COUNTRY_CONFIG[country] ?? {};
}
/** Shape of a US Employer Identification Number as sellers type it: `12-3456789`. */
const US_EIN_PATTERN = /^\d{2}-\d{7}$/;
/**
* Whether a US tax id typed by a seller is an EIN. Stripe takes any 9 digits as
* `company.tax_id` (a sole proprietor's SSN or ITIN included), so only the EIN
* shape with its dash is stored and attached as a `us_ein` tax id; any other
* shape goes to Stripe only.
*/
function isUsEin(value) {
	return US_EIN_PATTERN.test(value.trim());
}
function getVatConfig(country) {
	return COUNTRY_CONFIG[country]?.vat ?? null;
}
function getTaxIdOptions(country) {
	return COUNTRY_CONFIG[country]?.taxId ?? null;
}
function getDefaultTaxIdType(country) {
	return COUNTRY_CONFIG[country]?.taxId?.[0]?.type ?? null;
}
function getDefaultVatType(country) {
	return COUNTRY_CONFIG[country]?.vat?.type ?? null;
}
function showRegistrationField(country) {
	return !!COUNTRY_CONFIG[country]?.registration;
}
/** Whether the registration number is mandatory for the country, per its config.
*  Falls back to false for countries with no registration field (e.g. US and CA,
*  where the tax identifier is the business identifier). Each country's
*  registration.required is set explicitly. */
function isRegistrationRequired(country) {
	return COUNTRY_CONFIG[country]?.registration?.required ?? false;
}
function getRegistrationExample(country) {
	return COUNTRY_CONFIG[country]?.registration?.example ?? "";
}
/** When the registration number is also a Stripe Customer Tax ID (e.g. Swiss UID = ch_uid),
*  returns the type to use when attaching it to the Stripe invoice customer. */
function getRegistrationStripeType(country) {
	return COUNTRY_CONFIG[country]?.registration?.stripeType ?? null;
}
/**
* Returns true for non-EU countries where CardNexus has IOSS facilitator obligations.
* Used to conditionally show the IOSS acknowledgement + Art. 293A opt-in in onboarding.
*/
function isNonEuCountry(country) {
	return !EU_VAT_MEMBER_STATES.has(country);
}

//#endregion
//#region src/customs.ts
/**
* French overseas territories by postcode prefix, mapped to the country code
* carriers quote and label them under. Longest prefix first so Saint-Barthélemy
* and Saint-Martin win over Guadeloupe's `971`.
*/
const FRENCH_OVERSEAS_BY_POSTCODE_PREFIX = [
	["97133", "BL"],
	["97150", "MF"],
	["971", "GP"],
	["972", "MQ"],
	["973", "GF"],
	["974", "RE"],
	["975", "PM"],
	["976", "YT"],
	["986", "WF"],
	["987", "PF"],
	["988", "NC"]
];
/** Postcode prefixes of territories outside the EU customs area, per EU country. */
const SPECIAL_TERRITORY_POSTCODE_PREFIXES = {
	FR: ["97", "98"],
	ES: [
		"35",
		"38",
		"51",
		"52"
	]
};
/** Destinations whose labels need an ISO 3166-2 subdivision code: the North American markets. */
const STATE_CODE_COUNTRIES = new Set(NA_COUNTRIES);
/** Goods value (EUR cents) above which a postal CN22 / CN23 no longer suffices. */
const SELF_SHIP_CUSTOMS_ABOVE_EUR_CENTS = 1e5;
function normalizedPostcode(address) {
	return (address.postalCode ?? "").replace(/\s+/g, "");
}
/**
* The country code a carrier expects for an address: French overseas
* territories are addressed under their own code (`974xx` is `RE`), every
* other address keeps its country.
*/
function carrierCountryCode(address) {
	const country = address.country.toUpperCase();
	if (country !== "FR") return country;
	const postcode = normalizedPostcode(address);
	const match = FRENCH_OVERSEAS_BY_POSTCODE_PREFIX.find(([prefix]) => postcode.startsWith(prefix));
	return match ? match[1] : country;
}
/** Whether an address lies inside the EU customs area (EU VAT member states minus their special territories). */
function isInEuCustomsArea(address) {
	const country = address.country.toUpperCase();
	if (!EU_VAT_MEMBER_STATES.has(country)) return false;
	const prefixes = SPECIAL_TERRITORY_POSTCODE_PREFIXES[country];
	if (!prefixes) return true;
	const postcode = normalizedPostcode(address);
	return !prefixes.some((prefix) => postcode.startsWith(prefix));
}
/**
* Whether a parcel between the two addresses crosses a customs border and
* needs a customs declaration: the carrier countries differ and at least one
* side is outside the EU customs area.
*/
function isCustomsRoute(from, to) {
	if (carrierCountryCode(from) === carrierCountryCode(to)) return false;
	return !isInEuCustomsArea(from) || !isInEuCustomsArea(to);
}
/** Whether a label to this country must carry an ISO 3166-2 subdivision code. */
function requiresStateProvinceCode(country) {
	return STATE_CODE_COUNTRIES.has(country.toUpperCase());
}
/**
* The ISO 3166-2 code (`US-NY`) a carrier needs for a US or Canadian
* address, from the 2 to 3 letter code the buyer typed (with or without the
* country prefix, any case). `null` for any other country or when the stored
* state is not such a code.
*/
function stateProvinceCode(country, state) {
	const upperCountry = country.toUpperCase();
	if (!STATE_CODE_COUNTRIES.has(upperCountry)) return null;
	const code = (state ?? "").trim().toUpperCase().replace(new RegExp(`^${upperCountry}[-\\s]`), "");
	return /^[A-Z]{2,3}$/.test(code) ? `${upperCountry}-${code}` : null;
}
/**
* Whether a managed order ships without a CardNexus label because its goods
* value is above the postal customs ceiling: strictly above €1,000 on a
* customs route. The seller ships it themselves, tracked and signed for.
*/
function isSelfShipCustomsOrder(params) {
	if (!params.customsRoute) return false;
	if ((params.currency ?? "").toUpperCase() !== "EUR") return false;
	return params.goodsValueCents > SELF_SHIP_CUSTOMS_ABOVE_EUR_CENTS;
}
/**
* {@link isSelfShipCustomsOrder} from an order's facts: the route is read from
* the seller's ship-from country and postcode and the delivery address.
*/
function isCustomsSelfShippedOrder(params) {
	if (!params.sellerCountry || params.goodsValueCents == null) return false;
	return isSelfShipCustomsOrder({
		customsRoute: isCustomsRoute({
			country: params.sellerCountry,
			postalCode: params.sellerPostalCode
		}, params.destination),
		goodsValueCents: params.goodsValueCents,
		currency: params.currency
	});
}

//#endregion
//#region src/deep-link.ts
/**
* AppsFlyer OneLink payload keys carrying the deep-link destination.
* `deep_link_sub1` is a partner-template fallback for `deep_link_value`.
*/
const DEEP_LINK_VALUE_KEYS = ["deep_link_value", "deep_link_sub1"];
/**
* Leading path segment of a deep-link destination. One OneLink or push payload
* is read by both web and mobile, so the vocabulary is shared even though each
* platform resolves it to its own destination — a URL path on web, a navigator
* screen on mobile.
*/
const DEEP_LINK_ROUTES = {
	cart: "cart",
	cartWizard: "cart-wizard",
	chat: "chat",
	events: "events",
	explore: "explore",
	go: "go",
	inventory: "inventory",
	justListed: "just-listed",
	myBalance: "my-balance",
	negotiations: "negotiations",
	orders: "orders",
	photoRequests: "photo-requests",
	priceCheck: "price-check",
	products: "products",
	profile: "profile",
	quickSearch: "quick-search",
	reprice: "reprice",
	sales: "sales",
	scanner: "scanner",
	search: "search",
	sellerOnboarding: "seller-onboarding",
	settings: "settings",
	store: "store",
	users: "users"
};
/** Sub-segments of {@link DEEP_LINK_ROUTES.users} that open a tab of a public profile other than its listings. */
const DEEP_LINK_USER_PROFILE_SECTIONS = {
	collection: "collection",
	reviews: "reviews"
};
/** Sub-segment of {@link DEEP_LINK_ROUTES.profile} that opens the purchases list. */
const DEEP_LINK_PROFILE_PURCHASES = "purchases";
/** Sub-segment of {@link DEEP_LINK_ROUTES.profile} that opens referrals. */
const DEEP_LINK_PROFILE_REFERRALS = "referrals";
/** Sub-segment of {@link DEEP_LINK_ROUTES.profile} that opens profile settings. */
const DEEP_LINK_PROFILE_SETTINGS = "settings";
/** Sub-segment of {@link DEEP_LINK_ROUTES.profile} that opens preferences. */
const DEEP_LINK_PROFILE_PREFERENCES = "preferences";
/** Sub-segment of {@link DEEP_LINK_ROUTES.profile} that opens seller settings. */
const DEEP_LINK_PROFILE_SELLER = "seller";
/** Sub-segment of {@link DEEP_LINK_PROFILE_SELLER} for shipping settings. */
const DEEP_LINK_PROFILE_SHIPPING = "shipping";
/** Preference sections available through profile deep links. */
const DEEP_LINK_PREFERENCE_SECTIONS = {
	games: "games",
	inventory: "inventory",
	lists: "lists"
};
/** Shipping settings tabs available through seller profile deep links. */
const DEEP_LINK_SHIPPING_TABS = {
	rates: "rates",
	senderAddress: "sender-address",
	excludedCountries: "excluded-countries",
	excludedUsers: "excluded-users"
};
/** Account segment of the web settings URL. */
const DEEP_LINK_SETTINGS_ACCOUNT = "account";
/** Profile segment of the web account settings URL. */
const DEEP_LINK_SETTINGS_PROFILE = "profile";
/** Preferences segment of the web settings URL. */
const DEEP_LINK_SETTINGS_PREFERENCES = "preferences";
/** Selling segment of the web settings URL. */
const DEEP_LINK_SETTINGS_SELLING = "selling";
/** Shipping rates page segment of the web settings URL. */
const DEEP_LINK_SETTINGS_SHIPPING_RATES = "shipping-rates";
/** Settings subsection that contains the seller pause/unpause control. */
const DEEP_LINK_SETTINGS_SELLER_ACCOUNT = "seller-account";
/** Query param on {@link DEEP_LINK_ROUTES.justListed} narrowing the feed to one game. */
const DEEP_LINK_GAME_PARAM = "game";
/** Query param on {@link DEEP_LINK_ROUTES.search} limiting results to in-stock listings. */
const DEEP_LINK_IN_STOCK_PARAM = "inStock";
/** Query param on {@link DEEP_LINK_ROUTES.search} carrying the free-text search. */
const DEEP_LINK_QUERY_PARAM = "query";
/** Query param selecting an inventory-like surface's Collection or My Shop tab. */
const DEEP_LINK_TAB_PARAM = "tab";
/** Query param selecting where a scanner session saves its reviewed cards. */
const DEEP_LINK_DESTINATION_PARAM = "destination";
/** Query param on {@link DEEP_LINK_ROUTES.products} selecting a specific print. */
const DEEP_LINK_PRINT_NUMBER_PARAM = "printNumber";
/** Web-only query param carrying {@link DEEP_LINK_ROUTES.quickSearch} to its destination */
const DEEP_LINK_QUICK_SEARCH_PARAM = "quickSearch";
/**
* Query param opening the chat widget on any page. Carries a channel id
* (`order-{orderNumber}`, `negotiation-{id}`) or one of
* {@link DEEP_LINK_CHAT_SECTIONS} to land on a conversation list.
*/
const DEEP_LINK_CHAT_PARAM = "chat";
/** Values of {@link DEEP_LINK_CHAT_PARAM} that open a conversation list instead of a single channel. */
const DEEP_LINK_CHAT_SECTIONS = {
	orders: "orders",
	offers: "offers"
};

//#endregion
//#region src/dispute.ts
const ISSUE_CATEGORIES = [
	"missing_item",
	"wrong_condition",
	"counterfeit_resealed",
	"wrong_product",
	"damaged",
	"not_received",
	"other"
];
const DISPUTE_OUTCOMES = [
	"full_refund",
	"partial_refund",
	"no_refund",
	"return_refund",
	"goodwill_refund"
];
/** Days after shipping before a buyer can report an in-transit parcel as not received. */
const NOT_RECEIVED_WAIT_DAYS = 7;
const MS_PER_DAY$1 = 864e5;
/**
* Whether the buyer can pick the `not_received` issue category. Allowed as
* soon as tracking marks the order delivered; while it is still in transit,
* only once {@link NOT_RECEIVED_WAIT_DAYS} have passed since shipping.
*/
function canReportNotReceived(order, now = /* @__PURE__ */ new Date()) {
	if (order.status === "delivered") return true;
	if (!order.shippedAt) return false;
	return now.getTime() - new Date(order.shippedAt).getTime() >= NOT_RECEIVED_WAIT_DAYS * MS_PER_DAY$1;
}

//#endregion
//#region src/event-join-link.ts
/**
* The AppsFlyer OneLink CardNexus campaign links are built on: it opens the
* app when installed, otherwise sends to the App Store or Play Store and
* hands `deep_link_value` to the app on its first open.
*/
const CARDNEXUS_ONELINK_URL = "https://cardnexus.onelink.me/gE53/o5mkddvw";
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** The deep-link destination of an event: `events/<slug>`. */
function eventJoinDeepLinkValue(slug) {
	return `${DEEP_LINK_ROUTES.events}/${slug}`;
}
/**
* The link an event's join QR carries (booth TV, signage, flyers): a OneLink
* that opens the event in the app, or installs the app first.
*/
function buildEventJoinUrl(slug) {
	const url = new URL(CARDNEXUS_ONELINK_URL);
	url.searchParams.set(DEEP_LINK_VALUE_KEYS[0], eventJoinDeepLinkValue(slug));
	return url.toString();
}
function slugFromDestination(destination) {
	if (!destination) return null;
	const [route, slug] = destination.replace(/^\/+/, "").split(/[/?#]/);
	if (route !== DEEP_LINK_ROUTES.events || !slug) return null;
	return SLUG_PATTERN.test(slug) ? slug : null;
}
/**
* The event slug carried by a scanned join link, or null for any other text.
* Reads the OneLink (`deep_link_value` or `deep_link_sub1`), a web
* `/events/<slug>` URL (with or without a locale segment) and `cn://events/<slug>`.
*/
function parseEventJoinLink(text) {
	const trimmed = text.trim();
	if (trimmed.startsWith("cn://")) return slugFromDestination(trimmed.slice(5));
	let url;
	try {
		url = new URL(trimmed);
	} catch {
		return null;
	}
	for (const key of DEEP_LINK_VALUE_KEYS) {
		const slug = slugFromDestination(url.searchParams.get(key));
		if (slug) return slug;
	}
	const segments = url.pathname.split("/").filter(Boolean);
	const eventsIndex = segments.indexOf(DEEP_LINK_ROUTES.events);
	if (eventsIndex === -1) return null;
	return slugFromDestination(segments.slice(eventsIndex).join("/"));
}

//#endregion
//#region src/integration-app-category.ts
/**
* Curated categories for the public integrations directory. Staff assign one
* or more to each app in the admin; the directory filters and labels by them.
* Order is the display order of the directory's category navigation.
*/
const INTEGRATION_APP_CATEGORIES = [
	"inventory",
	"multichannel",
	"pricing",
	"collection",
	"deck-building",
	"scanning",
	"shipping",
	"point-of-sale",
	"analytics",
	"accounting",
	"community"
];
const IntegrationAppCategorySchema = z.enum(INTEGRATION_APP_CATEGORIES);
/** Narrows a stored category string to a category the directory still knows. */
function isIntegrationAppCategory(value) {
	return INTEGRATION_APP_CATEGORIES.includes(value);
}

//#endregion
//#region src/integration-app-listing.ts
/** Most directory categories one integration app can be filed under. */
const MAX_INTEGRATION_APP_CATEGORIES = 3;
/** Most screenshots one integration app can show in its directory gallery. */
const MAX_INTEGRATION_APP_SCREENSHOTS = 8;
/**
* The directory listing fields an app owner may propose changes to. Staff
* alone control the rest (slug, scopes, redirect URIs, status, whether the app
* is listed or featured, its order, credentials, privileges, rate limits).
*/
const INTEGRATION_APP_LISTING_FIELDS = [
	"name",
	"shortDescription",
	"longDescription",
	"logoUrl",
	"websiteUrl",
	"screenshots",
	"categories",
	"developerName",
	"supportUrl",
	"privacyPolicyUrl",
	"termsUrl",
	"games",
	"languages"
];
/**
* Lifecycle of an owner's proposed listing change. Only `pending` awaits
* staff. `accepted`, `partially_accepted` (some changed fields applied, the
* rest refused) and `rejected` are staff decisions; `withdrawn` covers a
* proposal the owner withdrew or replaced with a newer one.
*/
const INTEGRATION_APP_LISTING_PROPOSAL_STATUSES = [
	"pending",
	"accepted",
	"rejected",
	"withdrawn",
	"partially_accepted"
];
/** The statuses a staff decision leaves a listing proposal in. */
const INTEGRATION_APP_LISTING_PROPOSAL_DECISIONS = [
	"accepted",
	"partially_accepted",
	"rejected"
];
/**
* Whether staff decided the proposal and refused at least one changed field,
* so the owner may want to rework the refused values.
*/
function hasRefusedListingFields(status) {
	return status === "rejected" || status === "partially_accepted";
}
/** Longest staff note shown to the owner with a decision. */
const INTEGRATION_APP_LISTING_REVIEW_NOTE_MAX_LENGTH = 2e3;
/** Image types an owner may upload for their app's logo and screenshots. */
const INTEGRATION_APP_LISTING_IMAGE_TYPES = [
	"image/png",
	"image/jpeg",
	"image/webp"
];
/** Largest logo an owner may upload, in bytes. */
const INTEGRATION_APP_LOGO_MAX_BYTES = 2 * 1024 * 1024;
/** Largest screenshot an owner may upload, in bytes. */
const INTEGRATION_APP_SCREENSHOT_MAX_BYTES = 5 * 1024 * 1024;

//#endregion
//#region src/integration-app-scope-request.ts
/**
* Lifecycle of an owner's request for a different permission set. Only
* `pending` awaits staff; `accepted` and `rejected` are staff decisions,
* `withdrawn` covers a request the owner withdrew or replaced with a newer one.
*/
const INTEGRATION_APP_SCOPE_REQUEST_STATUSES = [
	"pending",
	"accepted",
	"rejected",
	"withdrawn"
];
/** Longest explanation an owner may give for the permissions they ask for. */
const INTEGRATION_APP_SCOPE_REQUEST_JUSTIFICATION_MAX_LENGTH = 2e3;
/** Longest staff note shown to the owner with a scope request decision. */
const INTEGRATION_APP_SCOPE_REQUEST_REVIEW_NOTE_MAX_LENGTH = 2e3;

//#endregion
//#region src/integration-app-slug.ts
/**
* An integration app slug: lowercase letters, digits and hyphens, starting
* with a letter or digit, 2 to 63 characters. It is the public connect-URL
* segment and never changes after the app is created.
*/
const INTEGRATION_APP_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,62}$/;
/** Longest integration app slug. */
const INTEGRATION_APP_SLUG_MAX_LENGTH = 63;
/** Whether `slug` follows the integration app slug rule. */
function isValidIntegrationAppSlug(slug) {
	return INTEGRATION_APP_SLUG_PATTERN.test(slug);
}
/**
* A slug suggestion from a name, e.g. `"Deckbox Sync!"` → `"deckbox-sync"`.
* The result may still be invalid (too short, or empty for a name with no
* letters or digits), so check it with `isValidIntegrationAppSlug`.
*/
function slugifyIntegrationAppName(name) {
	return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, INTEGRATION_APP_SLUG_MAX_LENGTH).replace(/-+$/, "");
}

//#endregion
//#region src/inventory.ts
let InventoryOperationType = /* @__PURE__ */ function(InventoryOperationType) {
	InventoryOperationType["BulkImport"] = "BulkImport";
	InventoryOperationType["ManualEdit"] = "ManualEdit";
	InventoryOperationType["Transaction"] = "Transaction";
	InventoryOperationType["List"] = "List";
	InventoryOperationType["OrderCancellationRestore"] = "OrderCancellationRestore";
	InventoryOperationType["Reprice"] = "Reprice";
	return InventoryOperationType;
}({});
let ValidateInventoryInputError = /* @__PURE__ */ function(ValidateInventoryInputError) {
	ValidateInventoryInputError["CARD_NOT_FOUND"] = "Card not found";
	ValidateInventoryInputError["INVALID_EXPANSION"] = "Invalid expansion";
	ValidateInventoryInputError["INVALID_VENDOR_ID_TYPE"] = "Invalid vendor id type";
	ValidateInventoryInputError["DUPLICATE_ENTRY"] = "Duplicate entry";
	ValidateInventoryInputError["LANGUAGE_NOT_AVAILABLE"] = "Language not available";
	ValidateInventoryInputError["FINISH_NOT_AVAILABLE"] = "Finish not available";
	ValidateInventoryInputError["MULTIPLE_CARDS_FOUND"] = "Multiple cards found";
	return ValidateInventoryInputError;
}({});

//#endregion
//#region src/marketplace.ts
/**
* Supported countries for marketplace operations.
* These are the countries where sellers can onboard.
*/
const SUPPORTED_MARKETPLACE_COUNTRIES = {
	EU: [
		"AT",
		"BE",
		"CH",
		"CY",
		"DK",
		"EE",
		"FI",
		"FR",
		"DE",
		"GB",
		"GR",
		"HR",
		"HU",
		"IE",
		"IT",
		"LT",
		"LU",
		"LV",
		"MT",
		"NL",
		"NO",
		"PL",
		"PT",
		"SE",
		"SI",
		"SK",
		"ES"
	],
	NA: ["US", "CA"]
};
const NA_COUNTRIES$1 = new Set(SUPPORTED_MARKETPLACE_COUNTRIES.NA);
/**
* Determines the marketplace region from a country code.
* NA = US, CA; everything else = EU.
*/
function getRegionFromCountry(countryCode) {
	return NA_COUNTRIES$1.has(countryCode) ? "NA" : "EU";
}
/**
* All supported marketplace countries as a flat array (seller onboarding).
*/
const ALL_SUPPORTED_MARKETPLACE_COUNTRIES = [...SUPPORTED_MARKETPLACE_COUNTRIES.EU, ...SUPPORTED_MARKETPLACE_COUNTRIES.NA];
/**
* Additional countries where buyers can receive deliveries but sellers cannot yet onboard.
* Currently empty — all previously buyer-only countries now have full seller support.
*/
const BUYER_ONLY_COUNTRIES = [];
/**
* All countries where buyers can have items delivered.
* Includes all marketplace countries plus buyer-only countries.
*/
const ALL_BUYER_DELIVERY_COUNTRIES = [...ALL_SUPPORTED_MARKETPLACE_COUNTRIES, ...BUYER_ONLY_COUNTRIES];
/** US and Canadian business addresses need a state/province for Stripe Tax and KYB. */
function requiresStateForBusinessAddress(countryCode) {
	return getRegionFromCountry(countryCode) === "NA";
}
/** USPS codes: the 50 states, DC, the inhabited territories and the military post offices. */
const US_STATE_CODES = new Set([
	"AL",
	"AK",
	"AZ",
	"AR",
	"CA",
	"CO",
	"CT",
	"DE",
	"FL",
	"GA",
	"HI",
	"ID",
	"IL",
	"IN",
	"IA",
	"KS",
	"KY",
	"LA",
	"ME",
	"MD",
	"MA",
	"MI",
	"MN",
	"MS",
	"MO",
	"MT",
	"NE",
	"NV",
	"NH",
	"NJ",
	"NM",
	"NY",
	"NC",
	"ND",
	"OH",
	"OK",
	"OR",
	"PA",
	"RI",
	"SC",
	"SD",
	"TN",
	"TX",
	"UT",
	"VT",
	"VA",
	"WA",
	"WV",
	"WI",
	"WY",
	"DC",
	"AS",
	"GU",
	"MP",
	"PR",
	"VI",
	"AA",
	"AE",
	"AP"
]);
/** Canada Post codes of the 10 provinces and 3 territories. */
const CA_PROVINCE_CODES = new Set([
	"AB",
	"BC",
	"MB",
	"NB",
	"NL",
	"NS",
	"NT",
	"NU",
	"ON",
	"PE",
	"QC",
	"SK",
	"YT"
]);
/**
* Whether a typed value (any case, spaces aside) is a known US state or
* Canadian province code: for `countryCode` when given (always `false` outside
* the US and Canada), for either country otherwise.
*/
function isStateCode(value, countryCode) {
	const code = value.trim().toUpperCase();
	if (countryCode === void 0) return US_STATE_CODES.has(code) || CA_PROVINCE_CODES.has(code);
	if (countryCode === "US") return US_STATE_CODES.has(code);
	if (countryCode === "CA") return CA_PROVINCE_CODES.has(code);
	return false;
}
/**
* The upper-cased state or province code of a US or Canadian address. `null`
* for any other country, which has no state field, or when the typed value is
* not a known code for the country.
*/
function stateCode(countryCode, state) {
	if (!state || !isStateCode(state, countryCode)) return null;
	return state.trim().toUpperCase();
}
/**
* The postal code, city and state line of an address, laid out for `countryCode`:
* `City, ST ZIP` in the US and Canada, `ZIP City, State` everywhere else (and
* when the country is unknown). The state is left out when empty.
*/
function formatCityLine(address, countryCode) {
	const { city, postalCode, state } = address;
	if (countryCode && getRegionFromCountry(countryCode.toUpperCase()) === "NA") return state ? `${city}, ${state} ${postalCode}` : `${city} ${postalCode}`;
	return state ? `${postalCode} ${city}, ${state}` : `${postalCode} ${city}`;
}
function hasText(value) {
	return typeof value === "string" && value.trim().length > 0;
}
function isBusinessAddressComplete(address) {
	if (!address) return false;
	if (!hasText(address.line1) || !hasText(address.city) || !hasText(address.postalCode) || !hasText(address.country)) return false;
	if (requiresStateForBusinessAddress(address.country)) return hasText(address.state);
	return true;
}

//#endregion
//#region src/mcp.ts
/** Slug of the first-party integration app that every MCP connection is granted to. */
const MCP_APP_SLUG = "cardnexus-mcp";
/**
* Every scope an MCP client can be granted, in consent-group order. It is the
* union of the scopes the MCP tools require plus `financial:read`, which only
* unlocks fee statements and a fee field. The `cardnexus-mcp` app is seeded
* with exactly this list, and it is the `scopes_supported` of the OAuth
* metadata.
*/
const MCP_SCOPE_BUNDLE = [
	"offers:read",
	"offers:write",
	"account:read",
	"account:write",
	"inventory:read",
	"inventory:write",
	"listings:read",
	"listings:write",
	"lists:read",
	"lists:write",
	"cart:read",
	"cart:write",
	"sales:read",
	"sales:write",
	"purchases:read",
	"purchases:write",
	"disputes:read",
	"disputes:write",
	"messaging:read",
	"messaging:write",
	"financial:read"
];
/**
* The public origin of the MCP host for an environment: production and dev
* have their own hosts, every other environment serves on the local
* public-api port. A non-empty `override` (a developer's tunnel) replaces the
* result; a trailing slash is dropped. It is also the OAuth issuer (`iss`).
*/
function mcpOrigin(environment, override) {
	const trimmed = override?.trim();
	if (trimmed) return trimmed.replace(/\/+$/, "");
	switch (environment) {
		case "prod":
		case "production": return "https://mcp.cardnexus.com";
		case "dev": return "https://mcp.dev.cardnexus.com";
		default: return "http://localhost:3002";
	}
}

//#endregion
//#region src/moderation.ts
const URL_SCHEME_RE = /(?:https?|ftp):\/\/|www\./i;
/**
* Bare domains with a TLD that phishing kits actually use. Deliberately not a
* full TLD list: "cards.Thanks" must not trip it, "5347567.shop" must.
*/
const BARE_DOMAIN_RE = /(?:^|[^a-z0-9@.-])[a-z0-9-]+(?:\.[a-z0-9-]+)*\.(?:com|net|org|info|biz|shop|store|site|online|xyz|top|club|link|click|live|vip|icu|buzz|page|world|app|io|co|me|ly|gg|tv|cc|to|ws|pw|fr|de|es|it|uk|us|eu|be|ch|nl|ca|ru|cn)(?![a-z0-9])/i;
/**
* True when a free-text message contains something a recipient could follow
* as a link — an explicit URL, a `www.` prefix, or a bare domain. Used to
* reject links in offer messages, where a link has no legitimate purpose.
*/
function containsLink(text) {
	return URL_SCHEME_RE.test(text) || BARE_DOMAIN_RE.test(text);
}
/**
* Substrings that make a username read as CardNexus staff or a platform
* notice. Matched against the lowercased username with separators removed,
* so `card_nexus-support` is caught as well as `cardnexussupport`.
*/
const RESERVED_USERNAME_TERMS = [
	"cardnexus",
	"support",
	"admin",
	"staff",
	"official",
	"moderator",
	"system",
	"notice",
	"security",
	"verification",
	"helpdesk"
];
function isReservedUsername(username) {
	const normalized = username.toLowerCase().replace(/[^a-z0-9]/g, "");
	return RESERVED_USERNAME_TERMS.some((term) => normalized.includes(term));
}

//#endregion
//#region src/negotiation-pricing.ts
/** Maximum discount from the original item subtotal, in percentage points. */
const NEGOTIATION_MAX_DISCOUNT_PERCENT = 40;
/** Minimum offered subtotal in minor currency units. */
function minimumOfferTotal(originalTotal) {
	return Math.ceil(originalTotal * (100 - NEGOTIATION_MAX_DISCOUNT_PERCENT) / 100);
}
/** Apply a percentage discount to the original subtotal, rounding to minor units. */
function quickOfferAmount(originalTotal, discount) {
	return Math.round(originalTotal * (100 - discount) / 100);
}
/**
* Splits a basket total across its lines as whole-cent unit prices. When the
* requested total is unreachable (a line's quantity is coarser than the
* remainder) the closest reachable total wins, except that the result never
* drops below the marketplace minimum — an offer typed at exactly the floor
* must not be rejected by a rounding cent. Callers should show
* `offerTotalOf(prices)` rather than the typed amount.
*/
function prorateOfferTotal(offerTotal, items) {
	if (items.length === 0) return [];
	const originalTotal = items.reduce((sum, item) => sum + item.originalUnitPrice * item.quantity, 0);
	if (originalTotal <= 0) return items.map((item) => ({
		inventoryLineId: item.inventoryLineId,
		offeredUnitPrice: 0
	}));
	const target = Math.min(offerTotal, originalTotal);
	const units = items.map((item) => {
		const lineShare = target * item.originalUnitPrice * item.quantity / originalTotal;
		return clampUnitPrice(Math.floor(lineShare / item.quantity), item.originalUnitPrice);
	});
	let deficit = target - items.reduce((sum, item, index) => sum + units[index] * item.quantity, 0);
	const byQuantity = items.map((_, index) => index).sort((a, b) => items[a].quantity - items[b].quantity);
	for (const index of byQuantity) {
		if (deficit <= 0) break;
		const item = items[index];
		const spare = item.originalUnitPrice - units[index];
		const add = Math.min(Math.floor(deficit / item.quantity), spare);
		units[index] += add;
		deficit -= add * item.quantity;
	}
	if (deficit > 0) {
		const index = byQuantity.find((candidate) => units[candidate] < items[candidate].originalUnitPrice);
		if (index !== void 0) {
			const overshoot = items[index].quantity - deficit;
			if (target - deficit < minimumOfferTotal(originalTotal) || overshoot < deficit) units[index] += 1;
		}
	}
	return items.map((item, index) => ({
		inventoryLineId: item.inventoryLineId,
		offeredUnitPrice: units[index]
	}));
}
/** The basket total a set of per-line prices actually offers. */
function offerTotalOf(prices, items) {
	const quantityByLine = new Map(items.map((item) => [item.inventoryLineId, item.quantity]));
	return prices.reduce((sum, price) => sum + price.offeredUnitPrice * (quantityByLine.get(price.inventoryLineId) ?? 0), 0);
}
function clampUnitPrice(unitPrice, originalUnitPrice) {
	return Math.min(Math.max(unitPrice, 0), originalUnitPrice);
}

//#endregion
//#region src/order.ts
const REFUND_REASONS = [
	"seller_no_ship",
	"item_not_as_described",
	"item_damaged",
	"item_not_received",
	"buyer_requested",
	"dispute_resolved",
	"seller_partial_refund",
	"other"
];
const TRACKING_STATUSES = [
	"pending",
	"in_transit",
	"out_for_delivery",
	"available_for_pickup",
	"delivered",
	"failed_attempt",
	"exception",
	"unknown"
];
const CANCELLATION_REASONS = [
	"item_unavailable",
	"item_damaged",
	"pricing_error",
	"seller_other",
	"changed_mind",
	"found_elsewhere",
	"taking_too_long",
	"buyer_other",
	"preorder_postponed",
	"auto_no_shipment"
];
/** Reasons a buyer can give when asking the seller to cancel an order. */
const BUYER_CANCEL_REASONS = [
	"changed_mind",
	"found_elsewhere",
	"taking_too_long",
	"buyer_other"
];
const TRACKING_CHANGE_REASONS = [
	"typo",
	"wrong_tracking_number",
	"new_label_issued",
	"combined_shipment",
	"other"
];
const FUND_OPERATION_TYPES = [
	"none",
	"refund",
	"transfer"
];
const ORDER_STATUS_ACTORS = [
	"buyer",
	"seller",
	"system",
	"admin"
];
/**
* Most seller-supplied metadata keys one order can carry. Enforced on the
* merged result of a write, so it bounds what is stored rather than what is
* sent.
*/
const ORDER_METADATA_MAX_KEYS = 50;
/**
* Order statuses that are truly final — no further transitions possible.
*/
const TERMINAL_ORDER_STATUSES = [
	"completed",
	"cancelled_seller",
	"cancelled_auto",
	"cancelled_buyer",
	"resolved_full_refund",
	"resolved_partial_refund",
	"resolved_no_refund",
	"resolved_return",
	"resolved_goodwill_refund"
];

//#endregion
//#region src/participant-qr.ts
/** Scheme and path of the QR a participant shows at the live-event booth. */
const PARTICIPANT_QR_PREFIX = "cn://user/";
const USER_ID_PATTERN = /[0-9a-f]{24}/i;
/** The payload of a participant's booth QR: their internal user id. */
function buildParticipantQrPayload(userId) {
	return `${PARTICIPANT_QR_PREFIX}${userId}`;
}
/**
* The user id carried by a scanned participant QR, or null when the text
* holds none. Lenient on purpose: a handheld scanner typing into the booth
* console on a non-QWERTY keyboard layout mangles the scheme's punctuation,
* so it only looks for the 24-character hex id.
*/
function parseParticipantQr(text) {
	const match = text.match(USER_ID_PATTERN);
	return match ? match[0].toLowerCase() : null;
}

//#endregion
//#region src/preorder.ts
/**
* Furthest a pre-order ship date may be set ahead of today when it is
* postponed, in calendar days.
*/
const PREORDER_MAX_LEAD_DAYS = 90;
/**
* Today's UTC calendar date as `YYYY-MM-DD`, the format of
* `expansions.release_date` and `orders.ships_on`.
*/
function utcToday(now = /* @__PURE__ */ new Date()) {
	return now.toISOString().slice(0, 10);
}
/**
* True when the `YYYY-MM-DD` day `date` falls strictly after `today`. Both
* are calendar dates, so a lexical comparison is exact.
*/
function isAfterDay(date, today) {
	return date > today;
}
/** Start of a `YYYY-MM-DD` calendar date, 00:00 UTC. */
function shipDateStart(date) {
	return /* @__PURE__ */ new Date(`${date}T00:00:00Z`);
}
function addCalendarDays(date, days) {
	const result = shipDateStart(date);
	result.setUTCDate(result.getUTCDate() + days);
	return utcToday(result);
}
/**
* The ship dates a postponement may set: from the day after the current ship
* date (or after today, when the order has none or it has passed) up to
* `PREORDER_MAX_LEAD_DAYS` after today.
*
* @param currentShipsOn - The order's ship date (`YYYY-MM-DD`); null on a regular order
*/
function postponeShipDateRange(currentShipsOn, today = utcToday()) {
	return {
		min: addCalendarDays(currentShipsOn && isAfterDay(currentShipsOn, today) ? currentShipsOn : today, 1),
		max: addCalendarDays(today, PREORDER_MAX_LEAD_DAYS)
	};
}

//#endregion
//#region src/promotions.ts
/**
* Kinds of seller promotion campaign:
* - `all_sellers`: every seller in the campaign's geography for its window.
* - `integration_linked`: sellers who connect the campaign's app during the
*   window, each for a fixed number of days from their own start.
* - `selected_sellers`: hand-picked sellers on negotiated conditions.
*/
const SELLER_PROMOTION_CAMPAIGN_KINDS = [
	"all_sellers",
	"integration_linked",
	"selected_sellers"
];
/**
* Standard seller fee per region in basis points (500 = 5%), charged on the
* listing price when no seller promotion applies.
*/
const STANDARD_SELLER_FEE_BPS = {
	EU: 500,
	NA: 800
};
/** Card-payment buyer fee percentage per region, applied to items + shipping. */
const BUYER_FEE_CONFIG = {
	NA: { buyerPercentage: .025 },
	EU: { buyerPercentage: .025 }
};
/**
* Card-payment buyer fixed fee per checkout currency in minor units (~€0.30
* equivalent, March 2026 exchange rates).
*/
const BUYER_FIXED_FEE = {
	[SupportedMarketplaceCurrency.EUR]: 30,
	[SupportedMarketplaceCurrency.USD]: 30,
	[SupportedMarketplaceCurrency.GBP]: 25,
	[SupportedMarketplaceCurrency.CAD]: 45,
	[SupportedMarketplaceCurrency.CHF]: 30,
	[SupportedMarketplaceCurrency.SEK]: 350,
	[SupportedMarketplaceCurrency.DKK]: 225,
	[SupportedMarketplaceCurrency.NOK]: 350,
	[SupportedMarketplaceCurrency.PLN]: 130,
	[SupportedMarketplaceCurrency.HUF]: 12e3
};
/** Balance-payment buyer fee percentage; balance payments carry no fixed fee. */
const BALANCE_PAYMENT_BUYER_FEE_PERCENTAGE = .01;
/**
* Promotion key slug: 2 to 63 lowercase letters, digits or hyphens, starting
* with a letter or digit. Keys identify promotions in the admin, the CLI and
* Braze (`current_seller_promo`).
*/
const PROMOTION_KEY_REGEX = /^[a-z0-9][a-z0-9-]{1,62}$/;
/**
* Who pays a buyer promotion's discount:
* - `cardnexus`: handled like a coupon, the seller payout is unchanged.
* - `shop`: the shop's items are sold at the reduced price, so the shop's
*   payout and the seller fee follow it.
* - `coop`: the shop pays a share of the discount as a price reduction and
*   CardNexus pays the rest like a coupon.
*/
const BUYER_PROMOTION_DISCOUNT_FUNDING = [
	"cardnexus",
	"shop",
	"coop"
];
const BPS_DENOMINATOR = 1e4;
/**
* The part of a buyer promotion's discount the shop pays, in basis points of
* the item price: the whole discount when shop-funded, its share (rounded
* down) when co-funded, 0 when CardNexus pays or there is no discount.
*
* @param shopShareBps - The shop's share of a co-funded discount in basis
*   points (5000 = half); ignored for the other fundings.
*/
function shopPaidDiscountBps(promotion) {
	const { discountBps, discountFundedBy, shopShareBps } = promotion;
	if (discountBps === null || discountFundedBy === "cardnexus") return 0;
	if (discountFundedBy === "shop") return discountBps;
	return Math.floor(discountBps * (shopShareBps ?? 0) / BPS_DENOMINATOR);
}
/**
* Languages a promotion's public name can be written in, English first.
* English is required; every other language is optional and falls back to it.
* Adding a language here offers it in the admin forms.
*/
const PROMOTION_NAME_LOCALES = [Locale.English, Locale.French];
/**
* The public name to show a viewer whose language is `locale`: that
* language's name when one was written, otherwise the English name.
*/
function resolvePromotionName(names, locale) {
	return (locale ? names[locale]?.trim() : void 0) || names[Locale.English];
}
/**
* A unit price after a buyer promotion's discount, in the same minor units:
* the reduction is rounded per unit, as checkout charges a shop-funded
* discount. Shown to buyers as the promoted price before the cart.
*
* @param discountBps - Discount in basis points (1000 = 10%).
*/
function promotedUnitPrice(price, discountBps) {
	return price - Math.round(price * discountBps / 1e4);
}

//#endregion
//#region src/rating.ts
/**
* Maximum rating value (1-5 scale).
*/
const MAX_RATING = 5;
/**
* Minimum rating value (1-5 scale).
*/
const MIN_RATING = 1;
/**
* Maximum comment length in characters.
*/
const MAX_COMMENT_LENGTH = 1e3;
/**
* Number of days after order completion that reviews can be submitted.
*/
const REVIEW_WINDOW_DAYS = 7;
/**
* Default rating used for auto-submitted reviews (5 stars).
*/
const AUTO_SUBMIT_DEFAULT_RATING = 5;
/**
* Rating written on every category of the automatic review a seller receives
* when an order is cancelled on their side (seller cancel or missed shipping
* deadline).
*/
const CANCELLATION_PENALTY_RATING = 1;
/**
* Number of days after a review is revealed that the reviewed party can appeal
* it.
*/
const APPEAL_WINDOW_DAYS = 30;
/**
* Which of the two reviews on an order an appeal contests. The appellant is
* always the party the targeted review is about: a seller appeals the
* `buyer_review`, a buyer appeals the `seller_review`.
*/
const REVIEW_APPEAL_TARGETS = ["buyer_review", "seller_review"];
/**
* Appeal lifecycle. `upheld` keeps the original review; `removed` drops its
* ratings from the appellant's averages. Both are terminal — an order's review
* can only ever be appealed once.
*/
const REVIEW_APPEAL_STATUSES = [
	"pending",
	"upheld",
	"removed"
];
/**
* The statuses staff can decide on — the lifecycle minus `pending`, which is
* the starting state rather than an outcome.
*/
const REVIEW_APPEAL_DECISIONS = ["upheld", "removed"];
/** Minimum length of the free-text reason an appellant must provide. */
const MIN_APPEAL_REASON_LENGTH = 10;
/** Maximum length of the free-text reason an appellant must provide. */
const MAX_APPEAL_REASON_LENGTH = 1e3;
/**
* Whether a terminal order status is eligible for reviews. Only terminal
* statuses are listed: a review window is opened from the `order.completed` and
* `order.dispute_resolved` events, so no other status can reach this.
*
* Adding a terminal order status fails typecheck until its eligibility is
* decided here.
*/
const REVIEW_ELIGIBILITY = {
	completed: true,
	cancelled_buyer: false,
	cancelled_seller: false,
	cancelled_auto: false,
	resolved_full_refund: true,
	resolved_partial_refund: true,
	resolved_no_refund: true,
	resolved_return: true,
	resolved_goodwill_refund: true
};
/**
* Order statuses that are eligible for reviews.
*/
const REVIEW_ELIGIBLE_STATUSES = TERMINAL_ORDER_STATUSES.filter((status) => REVIEW_ELIGIBILITY[status]);
/**
* Check if an order status is eligible for reviews.
*
* Accepts any order status, not just terminal ones: non-terminal statuses are
* never eligible.
*/
function isReviewEligibleStatus(status) {
	if (!Object.hasOwn(REVIEW_ELIGIBILITY, status)) return false;
	return REVIEW_ELIGIBILITY[status];
}

//#endregion
//#region src/redirect-uri.ts
/**
* Why a redirect URI was rejected at registration time. App owners register
* their own URIs from their integration settings, so every value here is
* rendered back to them as a translated message.
*/
const REDIRECT_URI_REJECTIONS = [
	"INVALID_URL",
	"INSECURE_SCHEME",
	"HAS_USERINFO",
	"HAS_FRAGMENT",
	"NON_ASCII_HOST",
	"IP_HOST"
];
const RedirectUriRejectionSchema = z.enum(REDIRECT_URI_REJECTIONS);
const RejectedRedirectUriSchema = z.object({
	uri: z.string(),
	reason: RedirectUriRejectionSchema
});
/** Upper bound on how many redirect URIs one integration app may register. */
const MAX_REDIRECT_URIS = 20;

//#endregion
//#region src/report-time-zone.ts
/**
* Maps ISO 3166-1 alpha-2 country codes to IANA timezone identifiers.
* Used to compute month boundaries in the seller's local time so orders are
* attributed to the calendar month the seller would expect. Both the monthly
* fee statements and the sales report read this one table.
* Countries with multiple timezones use their most common/business zone.
*/
const COUNTRY_TIMEZONE = {
	FR: "Europe/Paris",
	DE: "Europe/Berlin",
	AT: "Europe/Vienna",
	BE: "Europe/Brussels",
	NL: "Europe/Amsterdam",
	ES: "Europe/Madrid",
	IT: "Europe/Rome",
	PT: "Europe/Lisbon",
	GB: "Europe/London",
	IE: "Europe/Dublin",
	CH: "Europe/Zurich",
	PL: "Europe/Warsaw",
	CZ: "Europe/Prague",
	HU: "Europe/Budapest",
	RO: "Europe/Bucharest",
	SE: "Europe/Stockholm",
	NO: "Europe/Oslo",
	DK: "Europe/Copenhagen",
	FI: "Europe/Helsinki",
	GR: "Europe/Athens",
	US: "America/New_York",
	CA: "America/Toronto",
	AU: "Australia/Sydney",
	JP: "Asia/Tokyo",
	KR: "Asia/Seoul",
	SG: "Asia/Singapore",
	HK: "Asia/Hong_Kong"
};
/**
* The IANA time zone a seller of `country` reads reports in, or UTC when the
* country is unknown.
*
* @param country - ISO 3166-1 alpha-2 code of the seller's country
*/
function reportTimeZoneForCountry(country) {
	return (country ? COUNTRY_TIMEZONE[country] : void 0) ?? "UTC";
}

//#endregion
//#region src/reprice.ts
/**
* Price check groups, ordered from the most to the least overpriced. Values
* double as URL segments (`/price-check/<group>`). A card is above market
* only when it is also at least the minimum gap (`aboveMarketMinGapCents`)
* over its market price.
* - `way-above`: more than 30% above market.
* - `above`: 10% to 30% above market.
* - `at-market`: 10% under to 10% above market, or less than the minimum gap over it.
* - `good-deals`: 10% to 60% under market and worth the Good deals minimum.
* - `very-low`: more than 60% under market.
* - `no-market`: graded cards and cards without a market price.
*/
const PRICE_CHECK_GROUPS = [
	"way-above",
	"above",
	"at-market",
	"good-deals",
	"very-low",
	"no-market"
];
/** Groups a seller can fix from Price check with "Fix prices" / "Review prices". */
const FIXABLE_PRICE_CHECK_GROUPS = [
	"way-above",
	"above",
	"very-low"
];
/**
* Who may start a Price check or a reprice: nobody, the sellers on the
* allowlist, or every seller.
*/
const REPRICE_ACCESS_MODES = [
	"off",
	"allowlist",
	"everyone"
];
/**
* Lifecycle of a reprice run. `expired` is never stored: it is a `ready` run
* whose draft expiry has passed, derived by every reader. `superseded` is a
* never-applied draft that a newer draft replaced.
*/
const REPRICE_RUN_STATUSES = [
	"preparing",
	"ready",
	"applying",
	"applied",
	"undoing",
	"undone",
	"failed",
	"expired",
	"superseded"
];
/** Statuses during which a run holds the seller's single active slot. */
const ACTIVE_REPRICE_RUN_STATUSES = [
	"preparing",
	"applying",
	"undoing"
];
/**
* Which way prices may move: both ways, only down, or only up. A price rise
* removes the card from buyers' carts.
*/
const REPRICE_DIRECTIONS = [
	"both",
	"down",
	"up"
];
/**
* Price level a reprice targets: Sell fast (the configured percentage under
* market, shown as a Good deal), the market price, a custom percentage of the
* market price, or a percentage of each card's current sale price.
*/
const REPRICE_LEVEL_KINDS = [
	"sell-fast",
	"market",
	"custom",
	"percent-of-current"
];
/** Custom level bounds, as a percentage of the market price (−60% to +50%). */
const REPRICE_CUSTOM_PERCENT_MIN = -60;
const REPRICE_CUSTOM_PERCENT_MAX = 50;
const REPRICE_CUSTOM_PERCENT_STEP = 5;
/** Bounds of the `percent-of-current` level, as a percentage of the current sale price (any integer). */
const REPRICE_PERCENT_OF_CURRENT_MIN = -60;
const REPRICE_PERCENT_OF_CURRENT_MAX = 50;
/** Change of one reprice line: price lowered, raised, or newly put up for sale. */
const REPRICE_LINE_KINDS = [
	"down",
	"up",
	"new"
];
/**
* State of one reprice line:
* - `pending`: reviewed, not written yet.
* - `applied`: the new price is live.
* - `sold` / `changed`: skipped on apply because the card sold or its price
*   changed after the review.
* - `undone`: the previous price was restored.
* - `undo-skipped`: not restored, see the skip reason.
*/
const REPRICE_LINE_STATUSES = [
	"pending",
	"applied",
	"sold",
	"changed",
	"undone",
	"undo-skipped"
];
/** Why a line was not updated or not restored. */
const REPRICE_SKIP_REASONS = [
	"sold",
	"changed",
	"removed"
];
/** Tabs of the Review step. */
const REPRICE_CHANGE_TABS = [
	"all",
	"down",
	"up",
	"new",
	"promoted",
	"big",
	"left-out"
];
/** Sort orders of the Review step. */
const REPRICE_CHANGE_SORTS = [
	"biggest-drop",
	"biggest-rise",
	"highest-value",
	"name"
];
/** A change bigger than this share of the old price lands in "Big changes". */
const REPRICE_BIG_CHANGE_RATIO = .5;
/** Days a reprice can be undone after it was applied. */
const REPRICE_UNDO_DAYS = 7;

//#endregion
//#region src/risk.ts
const RISK_SEVERITIES = [
	"none",
	"info",
	"warning",
	"critical"
];
const RISK_INDICATOR_CODES = [
	"HIGH_VALUE",
	"FIRST_PURCHASE",
	"NEW_BUYER",
	"SAME_IP",
	"SAME_SUBNET",
	"SAME_CARD",
	"RECENT_LISTING",
	"INSTANT_LISTING",
	"ELEVATED_RISK",
	"CARD_COUNTRY_MISMATCH",
	"IP_COUNTRY_MISMATCH",
	"NEW_SELLER",
	"MULTIPLE_ORDERS_SAME_BUYER",
	"INSTANT_COMPLETION",
	"FAST_COMPLETION",
	"FAST_DELIVERY",
	"TRACKING_PREDATES_ORDER",
	"TRACKING_UNRECOGNIZED"
];

//#endregion
//#region src/seller.ts
const SellerType = {
	PRO: "pro",
	INDIVIDUAL: "individual"
};
const SELLER_TYPES = ["pro", "individual"];

//#endregion
//#region src/seller-inactivity.ts
/**
* Days without any seller action before the Braze inactivity pipeline
* starts (reminders at pause − 10/5/3/1 days).
*/
const SELLER_INACTIVITY_THRESHOLD_DAYS = 30;
/** Days of reminders between pipeline entry and the automatic pause. */
const SELLER_INACTIVITY_PAUSE_GRACE_DAYS = 10;
/**
* Date the automatic pause goes live. Sellers already past the schedule at
* launch are paused on this date instead, which gives Braze the full
* reminder ramp before it.
*/
const SELLER_INACTIVITY_PAUSE_START_AT = /* @__PURE__ */ new Date("2026-09-09T08:00:00Z");
const MS_PER_DAY = 864e5;
/**
* When a seller with this last activity gets paused if they stay inactive:
* `max(lastActivityAt + threshold + grace, go-live)`. Pushed to Braze as
* `seller_pause_at` and used by the daily sweep, so the reminders and the
* pause always agree on the date.
*/
function sellerPauseAt(lastActivityAt, pauseStartAt = SELLER_INACTIVITY_PAUSE_START_AT) {
	const scheduled = lastActivityAt.getTime() + (SELLER_INACTIVITY_THRESHOLD_DAYS + SELLER_INACTIVITY_PAUSE_GRACE_DAYS) * MS_PER_DAY;
	return new Date(Math.max(scheduled, pauseStartAt.getTime()));
}

//#endregion
//#region src/seller-stats.ts
const SELLER_STATS_PUBLIC_STATES = [
	"available",
	"new_seller",
	"quiet_seller"
];

//#endregion
//#region src/ship-batch.ts
/** The result of one order inside a ship batch. */
const SHIP_BATCH_OUTCOMES = [
	"pending",
	"shipped",
	"already_shipped",
	"failed",
	"waiting",
	"not_attempted"
];
/** Why one order of a ship batch failed or was not attempted. */
const SHIP_BATCH_ITEM_ERROR_CODES = [
	"NOT_FOUND",
	"INVALID_STATUS",
	"SHIPPING_MANAGED_BY_CARDNEXUS",
	"TRACKING_NUMBER_REQUIRED",
	"SIGNATURE_REQUIRED",
	"TRACKING_REGISTRATION_FAILED",
	"TRACKING_RATE_LIMITED",
	"INTERNAL_ERROR"
];
/** The lifecycle of a ship batch. */
const SHIP_BATCH_STATUSES = [
	"pending",
	"processing",
	"completed",
	"failed"
];

//#endregion
//#region src/shipment-tracking.ts
const SHIPMENT_TYPES = ["parcel", "letter"];
const REPORTED_TRACKING_STATUSES = [
	"label_created",
	"in_mailstream",
	"at_destination",
	"out_for_delivery",
	"delivered",
	"returned",
	"exception"
];
/** Tracking code (20 digits) plus a routing code of 0, 5, 9 or 11 digits. */
const IMB_LENGTHS = new Set([
	20,
	25,
	29,
	31
]);
/**
* Decode a USPS Intelligent Mail barcode tracking number, or return null when
* the value is not one.
*
* Only barcode identifiers starting with `0` are accepted: that is what the
* label tools print, and it keeps 20–31 digit parcel numbers from other
* carriers out.
*/
function parseIntelligentMailBarcode(trackingNumber) {
	const value = trackingNumber.trim();
	if (!/^\d+$/.test(value) || !IMB_LENGTHS.has(value.length)) return null;
	const barcodeId = value.slice(0, 2);
	if (barcodeId[0] !== "0" || Number(barcodeId[1]) > 4) return null;
	const serviceTypeId = value.slice(2, 5);
	const mailerIdLength = value[5] === "9" ? 9 : 6;
	const mailerId = value.slice(5, 5 + mailerIdLength);
	const serialNumber = value.slice(5 + mailerIdLength, 20);
	const routingCode = value.slice(20);
	return {
		barcodeId,
		serviceTypeId,
		mailerId,
		serialNumber,
		...routingCode.length > 0 && { zip5: routingCode.slice(0, 5) }
	};
}
/** The shipment type a tracking number implies. */
function shipmentTypeForTrackingNumber(trackingNumber) {
	return parseIntelligentMailBarcode(trackingNumber) ? "letter" : "parcel";
}

//#endregion
//#region src/shipping-mode-impact.ts
/**
* Resolves the shipping-mode impact copy for a seller, or `null` outside the
* countries where CardNexus-managed shipping exists (the choice is never shown
* there).
*/
function resolveShippingModeImpact(input) {
	if (!input.country || !isSendCloudActivatedCountry(input.country)) return null;
	const hasVatNumber = !!input.vatNumber?.trim() && input.notVatRegistered !== true;
	const profile = !input.isPro ? "individual" : hasVatNumber ? "proVat" : "proNoVat";
	return {
		countryKey: input.country.toUpperCase(),
		profile
	};
}

//#endregion
//#region src/shipping-rates.ts
/**
* Default shipping rates per country (in cents of seller's marketplace currency).
* All rates are based on TRACKED services only — every shipment on the marketplace must be tracked.
*
* Methodology:
* - Single source of truth per country: one tracked postal service per tier
* - Rates set at 100% of the actual retail tracked postal cost
* - "xsmall" uses cheapest tracked letter/packet for 1-3 cards (~20-50g)
* - "small" uses tracked letter/packet for 4-25 cards (~50-150g)
* - "medium" uses parcel services for 26-250 cards or single sealed products (~150g-1kg)
* - "large" uses parcel services for 251+ cards or display boxes (~1-2kg)
* - "xlarge" uses parcel services for cases (~3-5kg)
*
* Sellers can adjust rates freely in their settings (e.g. to cover packaging materials).
*
* Last updated: 2026-03 based on tracked service research of national postal services.
* See specs/shipping-rates-xs-redistribution/plan.md for full rationale.
* See specs/shipping-rates-tracked/knowledge-base.md for public-facing documentation.
*/
const DEFAULT_RATES = {
	FR: {
		domestic: {
			xsmall: 200,
			small: 360,
			medium: 690,
			large: 1120,
			xlarge: 1740
		},
		region: {
			xsmall: 510,
			small: 760,
			medium: 1500,
			large: 2220,
			xlarge: 2890
		},
		global: {
			xsmall: 560,
			small: 840,
			medium: 3520,
			large: 5500,
			xlarge: 7870
		}
	},
	DE: {
		domestic: {
			xsmall: 345,
			small: 415,
			medium: 620,
			large: 770,
			xlarge: 1050
		},
		region: {
			xsmall: 495,
			small: 560,
			medium: 1450,
			large: 1750,
			xlarge: 2250
		},
		global: {
			xsmall: 530,
			small: 690,
			medium: 2650,
			large: 4900,
			xlarge: 8e3
		}
	},
	ES: {
		domestic: {
			xsmall: 575,
			small: 655,
			medium: 1500,
			large: 1900,
			xlarge: 2375
		},
		region: {
			xsmall: 745,
			small: 830,
			medium: 1720,
			large: 2720,
			xlarge: 3500
		},
		global: {
			xsmall: 1020,
			small: 1180,
			medium: 3350,
			large: 5400,
			xlarge: 7850
		}
	},
	IT: {
		domestic: {
			xsmall: 600,
			small: 835,
			medium: 1030,
			large: 1100,
			xlarge: 1500
		},
		region: {
			xsmall: 770,
			small: 1135,
			medium: 2580,
			large: 3200,
			xlarge: 4500
		},
		global: {
			xsmall: 970,
			small: 1455,
			medium: 3950,
			large: 5900,
			xlarge: 8150
		}
	},
	NL: {
		domestic: {
			xsmall: 440,
			small: 440,
			medium: 745,
			large: 750,
			xlarge: 750
		},
		region: {
			xsmall: 970,
			small: 1050,
			medium: 1250,
			large: 2e3,
			xlarge: 2500
		},
		global: {
			xsmall: 1400,
			small: 1500,
			medium: 3220,
			large: 5150,
			xlarge: 8500
		}
	},
	BE: {
		domestic: {
			xsmall: 540,
			small: 540,
			medium: 710,
			large: 785,
			xlarge: 865
		},
		region: {
			xsmall: 1e3,
			small: 1100,
			medium: 1200,
			large: 1850,
			xlarge: 2500
		},
		global: {
			xsmall: 1080,
			small: 1160,
			medium: 1610,
			large: 3450,
			xlarge: 5e3
		}
	},
	AT: {
		domestic: {
			xsmall: 350,
			small: 405,
			medium: 770,
			large: 900,
			xlarge: 1280
		},
		region: {
			xsmall: 465,
			small: 745,
			medium: 2e3,
			large: 2500,
			xlarge: 3500
		},
		global: {
			xsmall: 540,
			small: 1450,
			medium: 3e3,
			large: 5e3,
			xlarge: 7850
		}
	},
	PT: {
		domestic: {
			xsmall: 375,
			small: 425,
			medium: 825,
			large: 1050,
			xlarge: 1555
		},
		region: {
			xsmall: 730,
			small: 810,
			medium: 3390,
			large: 3720,
			xlarge: 5250
		},
		global: {
			xsmall: 910,
			small: 1090,
			medium: 5850,
			large: 7830,
			xlarge: 1e4
		}
	},
	GR: {
		domestic: {
			xsmall: 400,
			small: 400,
			medium: 400,
			large: 500,
			xlarge: 1300
		},
		region: {
			xsmall: 750,
			small: 900,
			medium: 2500,
			large: 2900,
			xlarge: 4150
		},
		global: {
			xsmall: 900,
			small: 1200,
			medium: 3100,
			large: 4800,
			xlarge: 7e3
		}
	},
	IE: {
		domestic: {
			xsmall: 1e3,
			small: 1e3,
			medium: 1300,
			large: 1300,
			xlarge: 1700
		},
		region: {
			xsmall: 1700,
			small: 1800,
			medium: 3020,
			large: 3020,
			xlarge: 4e3
		},
		global: {
			xsmall: 2300,
			small: 2500,
			medium: 3620,
			large: 4e3,
			xlarge: 7e3
		}
	},
	FI: {
		domestic: {
			xsmall: 690,
			small: 690,
			medium: 890,
			large: 1090,
			xlarge: 1490
		},
		region: {
			xsmall: 1200,
			small: 1200,
			medium: 2090,
			large: 2890,
			xlarge: 3890
		},
		global: {
			xsmall: 1500,
			small: 1500,
			medium: 2290,
			large: 3600,
			xlarge: 5e3
		}
	},
	CY: {
		domestic: {
			xsmall: 350,
			small: 350,
			medium: 350,
			large: 350,
			xlarge: 490
		},
		region: {
			xsmall: 490,
			small: 665,
			medium: 990,
			large: 1690,
			xlarge: 2200
		},
		global: {
			xsmall: 540,
			small: 765,
			medium: 1190,
			large: 1990,
			xlarge: 2600
		}
	},
	EE: {
		domestic: {
			xsmall: 370,
			small: 490,
			medium: 670,
			large: 760,
			xlarge: 960
		},
		region: {
			xsmall: 800,
			small: 1295,
			medium: 1520,
			large: 1825,
			xlarge: 2800
		},
		global: {
			xsmall: 880,
			small: 1400,
			medium: 2395,
			large: 3200,
			xlarge: 5095
		}
	},
	HR: {
		domestic: {
			xsmall: 225,
			small: 600,
			medium: 600,
			large: 700,
			xlarge: 750
		},
		region: {
			xsmall: 995,
			small: 995,
			medium: 995,
			large: 1500,
			xlarge: 2e3
		},
		global: {
			xsmall: 1327,
			small: 1327,
			medium: 1327,
			large: 2e3,
			xlarge: 2700
		}
	},
	LV: {
		domestic: {
			xsmall: 496,
			small: 610,
			medium: 766,
			large: 924,
			xlarge: 1201
		},
		region: {
			xsmall: 800,
			small: 950,
			medium: 1100,
			large: 1288,
			xlarge: 1738
		},
		global: {
			xsmall: 1200,
			small: 1425,
			medium: 1650,
			large: 1932,
			xlarge: 2607
		}
	},
	LT: {
		domestic: {
			xsmall: 280,
			small: 310,
			medium: 623,
			large: 871,
			xlarge: 1615
		},
		region: {
			xsmall: 550,
			small: 650,
			medium: 900,
			large: 1200,
			xlarge: 1800
		},
		global: {
			xsmall: 825,
			small: 975,
			medium: 1350,
			large: 1800,
			xlarge: 2880
		}
	},
	LU: {
		domestic: {
			xsmall: 520,
			small: 640,
			medium: 880,
			large: 880,
			xlarge: 900
		},
		region: {
			xsmall: 720,
			small: 1060,
			medium: 1200,
			large: 1200,
			xlarge: 2e3
		},
		global: {
			xsmall: 790,
			small: 1270,
			medium: 3500,
			large: 3500,
			xlarge: 14e3
		}
	},
	MT: {
		domestic: {
			xsmall: 340,
			small: 385,
			medium: 580,
			large: 1e3,
			xlarge: 1800
		},
		region: {
			xsmall: 340,
			small: 450,
			medium: 2800,
			large: 3800,
			xlarge: 5500
		},
		global: {
			xsmall: 450,
			small: 650,
			medium: 5e3,
			large: 6500,
			xlarge: 9e3
		}
	},
	SK: {
		domestic: {
			xsmall: 290,
			small: 290,
			medium: 300,
			large: 300,
			xlarge: 340
		},
		region: {
			xsmall: 290,
			small: 290,
			medium: 1500,
			large: 1700,
			xlarge: 2200
		},
		global: {
			xsmall: 290,
			small: 300,
			medium: 2e3,
			large: 2400,
			xlarge: 3200
		}
	},
	SI: {
		domestic: {
			xsmall: 296,
			small: 305,
			medium: 412,
			large: 475,
			xlarge: 556
		},
		region: {
			xsmall: 712,
			small: 752,
			medium: 1200,
			large: 1600,
			xlarge: 2300
		},
		global: {
			xsmall: 792,
			small: 921,
			medium: 1800,
			large: 2500,
			xlarge: 3800
		}
	},
	HU: {
		domestic: {
			xsmall: 111500,
			small: 184e3,
			medium: 260500,
			large: 260500,
			xlarge: 295e3
		},
		region: {
			xsmall: 387500,
			small: 599e3,
			medium: 86e4,
			large: 86e4,
			xlarge: 101e4
		},
		global: {
			xsmall: 452e3,
			small: 642e3,
			medium: 95e4,
			large: 101e4,
			xlarge: 192e4
		}
	},
	PL: {
		domestic: {
			xsmall: 780,
			small: 780,
			medium: 1500,
			large: 1620,
			xlarge: 1700
		},
		region: {
			xsmall: 2200,
			small: 2600,
			medium: 4400,
			large: 7700,
			xlarge: 9500
		},
		global: {
			xsmall: 2300,
			small: 2700,
			medium: 5400,
			large: 10030,
			xlarge: 12e3
		}
	},
	SE: {
		domestic: {
			xsmall: 6100,
			small: 6900,
			medium: 10500,
			large: 14500,
			xlarge: 19e3
		},
		region: {
			xsmall: 15600,
			small: 20400,
			medium: 36500,
			large: 43e3,
			xlarge: 58500
		},
		global: {
			xsmall: 15600,
			small: 20400,
			medium: 36500,
			large: 43e3,
			xlarge: 58500
		}
	},
	DK: {
		domestic: {
			xsmall: 4500,
			small: 4500,
			medium: 4500,
			large: 5600,
			xlarge: 8200
		},
		region: {
			xsmall: 17500,
			small: 17500,
			medium: 17500,
			large: 17500,
			xlarge: 29800
		},
		global: {
			xsmall: 26900,
			small: 26900,
			medium: 26900,
			large: 26900,
			xlarge: 58300
		}
	},
	NO: {
		domestic: {
			xsmall: 7600,
			small: 7600,
			medium: 7600,
			large: 7600,
			xlarge: 7600
		},
		region: {
			xsmall: 15200,
			small: 16400,
			medium: 26900,
			large: 35100,
			xlarge: 49700
		},
		global: {
			xsmall: 18700,
			small: 21100,
			medium: 40400,
			large: 61400,
			xlarge: 91800
		}
	},
	GB: {
		domestic: {
			xsmall: 275,
			small: 275,
			medium: 355,
			large: 535,
			xlarge: 705
		},
		region: {
			xsmall: 1005,
			small: 1195,
			medium: 1350,
			large: 1750,
			xlarge: 2620
		},
		global: {
			xsmall: 1200,
			small: 1455,
			medium: 1990,
			large: 3175,
			xlarge: 5320
		}
	},
	CH: {
		domestic: {
			xsmall: 580,
			small: 580,
			medium: 900,
			large: 1205,
			xlarge: 2110
		},
		region: {
			xsmall: 950,
			small: 1e3,
			medium: 3600,
			large: 4005,
			xlarge: 5210
		},
		global: {
			xsmall: 950,
			small: 1e3,
			medium: 3600,
			large: 4005,
			xlarge: 5210
		}
	},
	US: {
		domestic: {
			xsmall: 730,
			small: 730,
			medium: 1e3,
			large: 1200,
			xlarge: 1475
		},
		region: {
			xsmall: 2040,
			small: 2400,
			medium: 2600,
			large: 4710,
			xlarge: 5900
		},
		global: {
			xsmall: 2330,
			small: 2700,
			medium: 3500,
			large: 6445,
			xlarge: 8e3
		}
	},
	CA: {
		domestic: {
			xsmall: 880,
			small: 880,
			medium: 1025,
			large: 1315,
			xlarge: 1825
		},
		region: {
			xsmall: 955,
			small: 955,
			medium: 1610,
			large: 2030,
			xlarge: 2425
		},
		global: {
			xsmall: 2220,
			small: 2400,
			medium: 4050,
			large: 4900,
			xlarge: 6065
		}
	}
};
/**
* Fallback rates if country not found in DEFAULT_RATES (in cents, EUR equivalent).
* Uses conservative rates (~75th percentile of EUR-marketplace countries) to account for unknowns.
* Based on tracked services only, at 100% of postal cost.
*
* Last updated: 2026-03 to align with new rate methodology.
*/
const FALLBACK_RATES = {
	domestic: {
		xsmall: 620,
		small: 680,
		medium: 900,
		large: 1150,
		xlarge: 1550
	},
	region: {
		xsmall: 1120,
		small: 1280,
		medium: 2300,
		large: 2950,
		xlarge: 4150
	},
	global: {
		xsmall: 1420,
		small: 1680,
		medium: 3300,
		large: 5250,
		xlarge: 7650
	}
};
/**
* Get default shipping rates for a country.
* Falls back to FALLBACK_RATES if country is not configured.
*/
function getDefaultRates(countryCode) {
	return DEFAULT_RATES[countryCode] ?? FALLBACK_RATES;
}

//#endregion
//#region src/stock-exporter.ts
/**
* The TCG Stock Exporter browser extension's Chrome Web Store listing.
* Every promo surface hides itself while this is `null`.
*/
const STOCK_EXPORTER_STORE_URL = "https://chromewebstore.google.com/detail/tcg-stock-exporter/nkipjbmekjbnjbcjicjkcnmeijjehnhg";
/** Redirect route used by every link from outside the app (emails, directory, Braze). */
const STOCK_EXPORTER_ENTRY_PATH = "/stock-exporter";
const STOCK_EXPORTER_INTEGRATION_SLUG = "tcg-stock-exporter";
/** Mirrors `apps/tcg-stock-exporter/src/shared/games/cardmarket-games.ts`. */
const STOCK_EXPORTER_SUPPORTED_GAMES = [
	"mtg",
	"pokemon",
	"onepiece",
	"fab",
	"swu",
	"riftbound",
	"lorcana"
];
const STOCK_EXPORTER_SUPPORTED_MARKETPLACES = ["Cardmarket"];
const STOCK_EXPORTER_SURFACES = [
	"csv_importer",
	"import_empty",
	"import_quick_add",
	"add_cards",
	"select_game_csv",
	"inventory_empty",
	"seller_onboarded",
	"sell_flow",
	"fees",
	"blog",
	"directory",
	"email"
];
const StockExporterSurfaceSchema = z.enum(STOCK_EXPORTER_SURFACES);
/** Exact header the extension writes for a marketplace export. */
const STOCK_EXPORTER_CSV_HEADER = [
	"Cardmarket ID",
	"Card Name",
	"Expansion",
	"Finish",
	"Condition",
	"Language",
	"Quantity",
	"Price",
	"Comments",
	"Signed",
	"Altered"
];
/** Currency of the prices in a marketplace export. */
const STOCK_EXPORTER_CSV_CURRENCY = "EUR";
/** True when the extension exports this game (slug from `STOCK_EXPORTER_SUPPORTED_GAMES`). */
function isStockExporterSupportedGame(gameSlug) {
	return STOCK_EXPORTER_SUPPORTED_GAMES.includes(gameSlug);
}
/** True when a CSV header row is exactly the extension's marketplace export. */
function isStockExporterCsvHeader(header) {
	const normalized = header.map((column) => column.trim());
	return normalized.length === STOCK_EXPORTER_CSV_HEADER.length && STOCK_EXPORTER_CSV_HEADER.every((column, i) => normalized[i] === column);
}
/**
* True when the extension can export from the user's price source. A missing
* price source (visitor, legacy user) counts as supported.
*/
function isStockExporterPriceSource(priceSource) {
	if (!priceSource) return true;
	return STOCK_EXPORTER_SUPPORTED_MARKETPLACES.includes(priceSource);
}
/**
* True when a seller with this price source can be offered the extension:
* the store listing exists and the price source is one it exports from.
*/
function isStockExporterOffered(priceSource) {
	return STOCK_EXPORTER_STORE_URL !== null && isStockExporterPriceSource(priceSource);
}
/** Game slug from the extension's file name (`<slug>-stock.csv`), if supported. */
function stockExporterGameFromFileName(fileName) {
	const slug = fileName.trim().match(/^([a-z0-9-]+)-stock(?:\s*\(\d+\))?\.csv$/i)?.[1]?.toLowerCase();
	return slug && isStockExporterSupportedGame(slug) ? slug : null;
}
/**
* Store listing URL tagged with `utm_source=cardnexus` and `utm_medium=<surface>`,
* or `null` while `STOCK_EXPORTER_STORE_URL` is unset.
*/
function buildStockExporterStoreUrl(surface) {
	const url = new URL(STOCK_EXPORTER_STORE_URL);
	url.searchParams.set("utm_source", "cardnexus");
	url.searchParams.set("utm_medium", surface);
	return url.toString();
}
/** Path (with optional locale prefix) of the redirect route, tagged with its source. */
function buildStockExporterEntryPath(source, locale) {
	return `${locale ? `/${locale}` : ""}${STOCK_EXPORTER_ENTRY_PATH}?src=${source}`;
}

//#endregion
//#region src/test-tracking-numbers.ts
/**
* Magic tracking numbers the non-production tracking fake recognises. Each one
* auto-progresses through a fixed status sequence (defined in
* `@repo/tracking-test`) via simulated Ship24 webhooks, so QA can drive an
* order to a chosen outcome without a real carrier.
*/
const TEST_TRACKING_NUMBERS = [
	"TEST_DELIVERED",
	"TEST_EXCEPTION",
	"TEST_FAILED_ATTEMPT",
	"TEST_PICKUP",
	"TEST_IN_TRANSIT",
	"TEST_UNKNOWN"
];
const DEFAULT_TEST_TRACKING_NUMBER = "TEST_DELIVERED";
function isKnownTestTrackingNumber(value) {
	return TEST_TRACKING_NUMBERS.includes(value);
}

//#endregion
//#region src/untracked-rates.ts
/**
* Default untracked letter rates per seller country (in cents of the seller's
* marketplace currency).
*
* Methodology: cheapest compliant untracked letter of the national postal
* service for ~20 g (xsmall, 1-3 cards) and ~100 g (small, 4-25 cards), plus a
* supplies margin, rounded up to a round figure of the currency, and always
* strictly below the tracked {@link DEFAULT_RATES} cell of the same zone/size.
*
* Region is `null` for DE (international letters are documents-only), CA (trading
* cards are banned from international letter-post) and US (no plain-stamp
* letter to Canada).
*
* Last updated: 2026-10 — see specs/untracked-shipping-v2/untracked-rates-research.md.
*/
const DEFAULT_UNTRACKED_RATES = {
	FR: {
		domestic: {
			xsmall: 190,
			small: 350
		},
		region: {
			xsmall: 270,
			small: 550
		}
	},
	DE: {
		domestic: {
			xsmall: 150,
			small: 250
		},
		region: null
	},
	ES: {
		domestic: {
			xsmall: 150,
			small: 250
		},
		region: {
			xsmall: 250,
			small: 370
		}
	},
	IT: {
		domestic: {
			xsmall: 170,
			small: 310
		},
		region: {
			xsmall: 180,
			small: 500
		}
	},
	NL: {
		domestic: {
			xsmall: 180,
			small: 430
		},
		region: {
			xsmall: 420,
			small: 810
		}
	},
	BE: {
		domestic: {
			xsmall: 210,
			small: 400
		},
		region: {
			xsmall: 350,
			small: 1e3
		}
	},
	AT: {
		domestic: {
			xsmall: 150,
			small: 220
		},
		region: {
			xsmall: 170,
			small: 730
		}
	},
	PT: {
		domestic: {
			xsmall: 200,
			small: 220
		},
		region: {
			xsmall: 310,
			small: 330
		}
	},
	GR: {
		domestic: {
			xsmall: 160,
			small: 280
		},
		region: {
			xsmall: 250,
			small: 360
		}
	},
	IE: {
		domestic: {
			xsmall: 230,
			small: 250
		},
		region: {
			xsmall: 400,
			small: 410
		}
	},
	FI: {
		domestic: {
			xsmall: 320,
			small: 610
		},
		region: {
			xsmall: 320,
			small: 610
		}
	},
	CY: {
		domestic: {
			xsmall: 80,
			small: 110
		},
		region: {
			xsmall: 110,
			small: 200
		}
	},
	EE: {
		domestic: {
			xsmall: 250,
			small: 270
		},
		region: {
			xsmall: 470,
			small: 500
		}
	},
	HR: {
		domestic: {
			xsmall: 120,
			small: 150
		},
		region: {
			xsmall: 210,
			small: 320
		}
	},
	LV: {
		domestic: {
			xsmall: 280,
			small: 320
		},
		region: {
			xsmall: 350,
			small: 450
		}
	},
	LT: {
		domestic: {
			xsmall: 250,
			small: 300
		},
		region: {
			xsmall: 250,
			small: 330
		}
	},
	LU: {
		domestic: {
			xsmall: 160,
			small: 300
		},
		region: {
			xsmall: 210,
			small: 570
		}
	},
	MT: {
		domestic: {
			xsmall: 100,
			small: 120
		},
		region: {
			xsmall: 200,
			small: 300
		}
	},
	SK: {
		domestic: {
			xsmall: 180,
			small: 280
		},
		region: {
			xsmall: 280,
			small: 280
		}
	},
	SI: {
		domestic: {
			xsmall: 290,
			small: 300
		},
		region: {
			xsmall: 500,
			small: 520
		}
	},
	HU: {
		domestic: {
			xsmall: 5e4,
			small: 12e4
		},
		region: {
			xsmall: 135e3,
			small: 38e4
		}
	},
	PL: {
		domestic: {
			xsmall: 700,
			small: 750
		},
		region: {
			xsmall: 1200,
			small: 1400
		}
	},
	SE: {
		domestic: {
			xsmall: 3e3,
			small: 5500
		},
		region: {
			xsmall: 5e3,
			small: 7500
		}
	},
	DK: {
		domestic: {
			xsmall: 3e3,
			small: 3e3
		},
		region: {
			xsmall: 5e3,
			small: 5500
		}
	},
	NO: {
		domestic: {
			xsmall: 3500,
			small: 5500
		},
		region: {
			xsmall: 4500,
			small: 7e3
		}
	},
	GB: {
		domestic: {
			xsmall: 130,
			small: 150
		},
		region: {
			xsmall: 400,
			small: 410
		}
	},
	CH: {
		domestic: {
			xsmall: 150,
			small: 160
		},
		region: {
			xsmall: 230,
			small: 500
		}
	},
	US: {
		domestic: {
			xsmall: 130,
			small: 320
		},
		region: null
	},
	CA: {
		domestic: {
			xsmall: 150,
			small: 250
		},
		region: null
	}
};
/**
* Fallback untracked rates when the country is not in
* {@link DEFAULT_UNTRACKED_RATES} (in cents, EUR equivalent, ~75th percentile of
* the EUR countries).
*/
const FALLBACK_UNTRACKED_RATES = {
	domestic: {
		xsmall: 250,
		small: 320
	},
	region: {
		xsmall: 350,
		small: 560
	}
};
/**
* Get the default untracked letter rates for a seller country (ISO-2,
* case-insensitive). Falls back to {@link FALLBACK_UNTRACKED_RATES}.
*/
function getDefaultUntrackedRates(countryCode) {
	return DEFAULT_UNTRACKED_RATES[countryCode.toUpperCase()] ?? FALLBACK_UNTRACKED_RATES;
}

//#endregion
//#region src/utm.ts
const UTM_KEYS = [
	"utm_source",
	"utm_medium",
	"utm_campaign"
];
const utmParamsSchema = z.object({
	utm_source: z.string().optional(),
	utm_medium: z.string().optional(),
	utm_campaign: z.string().optional()
});

//#endregion
export { ACTIVE_REPRICE_RUN_STATUSES, AFFILIATE_AF_ID_PARAM, AFFILIATE_CLICK_ID_PARAM, AFFILIATE_ID_SOURCES, AFFILIATE_LANDING_PARAMS, AFFILIATE_LINK_HOST, ALL_BUYER_DELIVERY_COUNTRIES, ALL_SCOPE_FEATURE_FLAGS, ALL_SUPPORTED_MARKETPLACE_COUNTRIES, API_ACCESS_KINDS, API_ACCESS_REQUEST_API_AREAS, API_ACCESS_REQUEST_AUDIENCES, API_ACCESS_REQUEST_DECISIONS, API_ACCESS_REQUEST_DECISION_NOTES_MAX_LENGTH, API_ACCESS_REQUEST_DESCRIPTION_MAX_LENGTH, API_ACCESS_REQUEST_DISCORD_GRANT_STATUSES, API_ACCESS_REQUEST_DISCORD_USERNAME_MAX_LENGTH, API_ACCESS_REQUEST_MESSAGE_AUTHOR_TYPES, API_ACCESS_REQUEST_MESSAGE_MAX_LENGTH, API_ACCESS_REQUEST_OPEN_STATUSES, API_ACCESS_REQUEST_PROJECT_NAME_MAX_LENGTH, API_ACCESS_REQUEST_REJECTION_REASON_MAX_LENGTH, API_ACCESS_REQUEST_SOURCES, API_ACCESS_REQUEST_STATUSES, API_ACCESS_REQUEST_TOOL_CATEGORIES, API_ACCESS_REQUEST_WEBSITE_URL_MAX_LENGTH, API_DATA_ACCESS_CHANGES, API_DATA_APPLICATION_AUDIENCES, API_DATA_APPLICATION_BENEFITS, API_DATA_APPLICATION_DAILY_CALLS, API_DATA_APPLICATION_LIMITS, API_DATA_APPLICATION_MONTHLY_USERS, API_DATA_PERMISSIONS, API_DATA_PERMISSION_GRANT_SOURCES, API_DATA_PERMISSION_NOTE_MAX_LENGTH, API_DATA_PERMISSION_REQUEST_STATUSES, API_DATA_PERMISSION_STATES, APPEAL_WINDOW_DAYS, AUTO_SUBMIT_DEFAULT_RATING, AccountStatusSchema, ApiDataAccessChangeSchema, ApiDataAccessRequestApplicationSchema, ApiDataApplicationAudienceSchema, ApiDataApplicationBenefitSchema, ApiDataApplicationBenefitsSchema, ApiDataApplicationCommitmentsSchema, ApiDataApplicationDailyCallsSchema, ApiDataApplicationMonthlyUsersSchema, ApiDataApplicationProjectSchema, ApiDataApplicationSchema, ApiDataApplicationUsageSchema, ApiDataPermissionGrantSourceSchema, ApiDataPermissionHolderSchema, ApiDataPermissionRequestStatusSchema, ApiDataPermissionSchema, BALANCE_PAYMENT_BUYER_FEE_PERCENTAGE, BUYER_ACCOUNT_STATUSES, BUYER_CANCEL_REASONS, BUYER_FEE_CONFIG, BUYER_FIXED_FEE, BUYER_ONLY_COUNTRIES, BUYER_PROMOTION_DISCOUNT_FUNDING, BuyerAccountStatusValueSchema, CANCELLATION_PENALTY_RATING, CANCELLATION_REASONS, CARDNEXUS_ONELINK_URL, COUNTRY_TIMEZONE, COUPON_CAMPAIGN_CODE_PREFIX_REGEX, COUPON_CAMPAIGN_DISPLAY_STATUSES, COUPON_CAMPAIGN_FULL_SHOP_SHARE_BPS, COUPON_CAMPAIGN_LIMITS, COUPON_CAMPAIGN_MESSAGE_AUTHOR_TYPES, COUPON_CAMPAIGN_OPEN_STATUSES, COUPON_CAMPAIGN_PHASES, COUPON_CAMPAIGN_STATUSES, COUPON_CAMPAIGN_TIME_ZONES, DEEP_LINK_CHAT_PARAM, DEEP_LINK_CHAT_SECTIONS, DEEP_LINK_DESTINATION_PARAM, DEEP_LINK_GAME_PARAM, DEEP_LINK_IN_STOCK_PARAM, DEEP_LINK_PREFERENCE_SECTIONS, DEEP_LINK_PRINT_NUMBER_PARAM, DEEP_LINK_PROFILE_PREFERENCES, DEEP_LINK_PROFILE_PURCHASES, DEEP_LINK_PROFILE_REFERRALS, DEEP_LINK_PROFILE_SELLER, DEEP_LINK_PROFILE_SETTINGS, DEEP_LINK_PROFILE_SHIPPING, DEEP_LINK_QUERY_PARAM, DEEP_LINK_QUICK_SEARCH_PARAM, DEEP_LINK_ROUTES, DEEP_LINK_SETTINGS_ACCOUNT, DEEP_LINK_SETTINGS_PREFERENCES, DEEP_LINK_SETTINGS_PROFILE, DEEP_LINK_SETTINGS_SELLER_ACCOUNT, DEEP_LINK_SETTINGS_SELLING, DEEP_LINK_SETTINGS_SHIPPING_RATES, DEEP_LINK_SHIPPING_TABS, DEEP_LINK_TAB_PARAM, DEEP_LINK_USER_PROFILE_SECTIONS, DEEP_LINK_VALUE_KEYS, DEFAULT_RATES, DEFAULT_TEST_TRACKING_NUMBER, DEFAULT_UNTRACKED_RATES, DISPUTE_OUTCOMES, EU_COUNTRIES, EU_IOSS_VAT_RATES, EU_VAT_MEMBER_STATES, FACILITATOR_MECHANISMS, FACILITATOR_THRESHOLDS, FALLBACK_RATES, FALLBACK_UNTRACKED_RATES, FEE_PARTY_TREATMENTS, FEE_VAT_TYPES, FIXABLE_PRICE_CHECK_GROUPS, FUND_OPERATION_TYPES, INTEGRATION_APP_CATEGORIES, INTEGRATION_APP_LISTING_FIELDS, INTEGRATION_APP_LISTING_IMAGE_TYPES, INTEGRATION_APP_LISTING_PROPOSAL_DECISIONS, INTEGRATION_APP_LISTING_PROPOSAL_STATUSES, INTEGRATION_APP_LISTING_REVIEW_NOTE_MAX_LENGTH, INTEGRATION_APP_LOGO_MAX_BYTES, INTEGRATION_APP_SCOPE_REQUEST_JUSTIFICATION_MAX_LENGTH, INTEGRATION_APP_SCOPE_REQUEST_REVIEW_NOTE_MAX_LENGTH, INTEGRATION_APP_SCOPE_REQUEST_STATUSES, INTEGRATION_APP_SCREENSHOT_MAX_BYTES, INTEGRATION_APP_SLUG_MAX_LENGTH, INTEGRATION_APP_SLUG_PATTERN, INTEGRATION_GRANTABLE_SCOPES, ISSUE_CATEGORIES, IntegrationAppCategorySchema, InventoryOperationType, MAX_APPEAL_REASON_LENGTH, MAX_COMMENT_LENGTH, MAX_INTEGRATION_APP_CATEGORIES, MAX_INTEGRATION_APP_SCREENSHOTS, MAX_RATES, MAX_RATING, MAX_REDIRECT_URIS, MAX_UNTRACKED_RATES, MCP_APP_SLUG, MCP_SCOPE_BUNDLE, MIN_APPEAL_REASON_LENGTH, MIN_RATING, NA_COUNTRIES, NEGOTIATION_MAX_DISCOUNT_PERCENT, NON_EU_VAT_RATES, NON_GRANTABLE_SCOPES, NOT_RECEIVED_WAIT_DAYS, ORDER_METADATA_MAX_KEYS, ORDER_STATUS_ACTORS, PACKAGE_SIZES, PACKAGE_SIZE_DEFINITIONS, PARTICIPANT_QR_PREFIX, PREORDER_MAX_LEAD_DAYS, PRICE_CHECK_GROUPS, PROMOTION_KEY_REGEX, PROMOTION_NAME_LOCALES, PUBLIC_API_SCOPES, PUBLIC_API_SCOPE_GROUPS, PublicApiScopeSchema, REDIRECT_URI_REJECTIONS, REFUND_REASONS, REGISTRATION_STRIPE_TYPE, REPORTED_TRACKING_STATUSES, REPRICE_ACCESS_MODES, REPRICE_BIG_CHANGE_RATIO, REPRICE_CHANGE_SORTS, REPRICE_CHANGE_TABS, REPRICE_CUSTOM_PERCENT_MAX, REPRICE_CUSTOM_PERCENT_MIN, REPRICE_CUSTOM_PERCENT_STEP, REPRICE_DIRECTIONS, REPRICE_LEVEL_KINDS, REPRICE_LINE_KINDS, REPRICE_LINE_STATUSES, REPRICE_PERCENT_OF_CURRENT_MAX, REPRICE_PERCENT_OF_CURRENT_MIN, REPRICE_RUN_STATUSES, REPRICE_SKIP_REASONS, REPRICE_UNDO_DAYS, RESERVED_USERNAME_TERMS, REVIEW_APPEAL_DECISIONS, REVIEW_APPEAL_STATUSES, REVIEW_APPEAL_TARGETS, REVIEW_ELIGIBLE_STATUSES, REVIEW_WINDOW_DAYS, RISK_INDICATOR_CODES, RISK_SEVERITIES, RedirectUriRejectionSchema, RejectedRedirectUriSchema, SEALED_CASE_MARKER, SEALED_FALLBACK_SIZE, SEALED_PRODUCT_TYPE_FAMILY, SELF_SHIP_CUSTOMS_ABOVE_EUR_CENTS, SELLER_ACCOUNT_STATUSES, SELLER_INACTIVITY_PAUSE_GRACE_DAYS, SELLER_INACTIVITY_PAUSE_START_AT, SELLER_INACTIVITY_THRESHOLD_DAYS, SELLER_PROMOTION_CAMPAIGN_KINDS, SELLER_SHIPPING_MODES, SELLER_STATS_PUBLIC_STATES, SELLER_TYPES, SENDCLOUD_ACTIVATED_COUNTRIES, SHIPMENT_TYPES, SHIPPING_SERVICES, SHIPPING_ZONES, SHIP_BATCH_ITEM_ERROR_CODES, SHIP_BATCH_OUTCOMES, SHIP_BATCH_STATUSES, STANDARD_SELLER_FEE_BPS, STATIC_VAT_RATES, STOCK_EXPORTER_CSV_CURRENCY, STOCK_EXPORTER_CSV_HEADER, STOCK_EXPORTER_ENTRY_PATH, STOCK_EXPORTER_INTEGRATION_SLUG, STOCK_EXPORTER_STORE_URL, STOCK_EXPORTER_SUPPORTED_GAMES, STOCK_EXPORTER_SUPPORTED_MARKETPLACES, STOCK_EXPORTER_SURFACES, STRIPE_CUSTOMER_TAX_ID_TYPES, STRIPE_ONBOARDING_STATUSES, SUPPORTED_MARKETPLACE_COUNTRIES, SealedProductFamily, SealedProductType, SellerAccountStatusValueSchema, SellerType, StockExporterSurfaceSchema, StripeCustomerTaxIdType, TERMINAL_ORDER_STATUSES, TEST_TRACKING_NUMBERS, TRACKING_CHANGE_REASONS, TRACKING_STATUSES, UNTRACKED_PACKAGE_SIZES, UNTRACKED_ZONES, USER_FLAG, USER_FLAGS, US_EIN_PATTERN, UTM_KEYS, VAT_BASES, VAT_CLASSIFICATION_FLAGS, VAT_CLASSIFICATION_VERSION, VAT_LINE_EVENT_KINDS, VAT_RATE_SOURCES, VAT_SCHEMES, VAT_TAX_ID_TYPES, ValidateInventoryInputError, apiDataApplicationIssues, buildEventJoinUrl, buildParticipantQrPayload, buildStockExporterEntryPath, buildStockExporterStoreUrl, canReportNotReceived, canShipTo, carrierCountryCode, classifyAdminIdentifier, compareAddresses, computeShipsTo, containsLink, couponCampaignDisplayStatus, couponCampaignPhase, eventJoinDeepLinkValue, exclusiveVat, facilitatorCandidate, facilitatorRateCountry, facilitatorRegime, formatCityLine, getCountryConfig, getDefaultRates, getDefaultTaxIdType, getDefaultUntrackedRates, getDefaultVatType, getRegionCountries, getRegionFromCountry, getRegionKey, getRegistrationExample, getRegistrationStripeType, getShippingZone, getTaxIdOptions, getVatConfig, hasRefusedListingFields, isAffiliateIdSource, isAfterDay, isBusinessAddressComplete, isCustomsRoute, isCustomsSelfShippedOrder, isInEuCustomsArea, isIntegrationAppCategory, isKnownTestTrackingNumber, isNonEuCountry, isOpenApiAccessRequestStatus, isOpenCouponCampaignStatus, isRegistrationRequired, isReservedUsername, isReviewEligibleStatus, isSealedProductType, isSelfShipCustomsOrder, isSendCloudActivatedCountry, isShippingManagedByCardNexus, isStateCode, isStockExporterCsvHeader, isStockExporterOffered, isStockExporterPriceSource, isStockExporterSupportedGame, isUsEin, isValidIntegrationAppSlug, mcpOrigin, minimumOfferTotal, offerTotalOf, parseAffiliateLandingParams, parseAffiliateLinkPath, parseEventJoinLink, parseIntelligentMailBarcode, parseParticipantQr, postponeShipDateRange, promotedUnitPrice, prorateOfferTotal, quickOfferAmount, readAffiliateAttribution, reportTimeZoneForCountry, requestedAccessForAudience, requiresStateForBusinessAddress, requiresStateProvinceCode, resolveApiDataPermissionStates, resolvePromotionName, resolveShippingModeImpact, scopeFeatureFlag, scopesToFeatureFlags, sealedProductFamily, sealedProductTypesInFamily, sellerPauseAt, shipDateStart, shipmentTypeForTrackingNumber, shopPaidDiscountBps, showRegistrationField, slugifyIntegrationAppName, splitInclusiveVat, stateCode, stateProvinceCode, stockExporterGameFromFileName, summarizeGrantedScopes, toStripeTaxIdType, utcToday, utmParamsSchema, withinFacilitatorThreshold };
//# sourceMappingURL=index.js.map